import React from 'react';
import { useQuery } from '@tanstack/react-query';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Paper,
  Popover,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
// import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CircularProgress from '@mui/material/CircularProgress';
import { formatDate } from '@/utils/date-utils';
import {
  getEditableMedicalConditionValues,
  mapMedicalConditionToApiValue,
  mapMedicalConditionsToApiValues,
  resolveMedicalConditionLabel,
} from '@/utils/client-medical-conditions';
import { getValidAllergies, getValidCuisineTypes, getValidFoodIntolerances, getValidPreferences } from '@/lib/clientPreferenceOptions';
import { normalizeFoodIntoleranceSelection } from '@/utils/food-intolerances';
import { MEDICAL_CONDITIONS_API_OPTIONS } from '@/constants/medical-conditions';
import { getCalculationFormulas, getViableFormulas } from '@/lib/api/clients';
import { getCurrentUser } from '@/lib/api/auth';
import { getQuestionnaireResponses, type QuestionnaireInstanceResponse } from '@/lib/api/questionnaires';
import { getAppointments } from '@/lib/api/appointments';
import { updateClient } from '@/lib/apiService';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { getCountryCode } from '@/utils/country-code-mapping';
import {
  getCountryCodeFromDialCode,
  getDialCodeForCountryCode,
  getPhoneCountryFromLocation,
} from '@/utils/country-phone-mapping';
import {
  getActivityLevelOptionByValue,
  getActivityLevelSelectOptions,
  resolveAllowedActivityLevels,
  type ResolvedActivityLevels,
} from '@/utils/activity-levels';
import {
  GENERAL_WEIGHT_GOAL_OPTIONS,
  LACTATION_STATUS_OPTIONS,
  PREGNANCY_TRIMESTER_OPTIONS,
  PREGNANCY_TYPE_OPTIONS,
  WEIGHT_LOSS_GOAL_OPTIONS,
  DIABETES_TYPE_OPTIONS,
  THYROID_TYPE_OPTIONS,
} from '@/clients/onboarding/components/constants';
import { calculateAgeFromDateString, calculateAgeInMonthsFromDateString } from '@/clients/onboarding/components/date-utils';
import {
  getFormulaDisplayName,
  getFormulaFallbackDisplayName,
  getPreferredFormulaFallback,
  isIomFormulaSelection,
} from '@/clients/onboarding/components/formula-utils';
import FormulaValidationModal from '@/components/clients/shared/FormulaValidationModal';
import countryOptions from '@/data/countries.json';
import expandedPanelGraphic from '@/assets/images/client-details-expanded-panel.svg';
import layer1Graphic from '@/assets/images/layer-1.svg';
import { SearchableTagSelector } from '@/components/shared/SearchableTagSelector';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import type { Client } from '@/types/client';
import { isPendingDeletionClient } from '@/utils/client-account-deletion';
import type { CalculationFormula, DRVGuideline } from '@/types/calculation-formulas';
import { formatDisplayLabel } from '@/utils/display-labels';
import { resolveClientTargetCalories } from '@/utils/client-goals';
import { getMacroGoalHint, type MacroGoalNutrient } from '@/utils/macro-goal-guidance';
import type { AboutDetails, ActiveSubscriptionSummary, ClientGoalRow, DetailMetricItem, MetricItem, SummaryItem } from './clientDetailsV2.utils';
import { EMPTY_CLIENT_DETAIL_VALUE, blueSectionHeadingFontSize, findOngoingVisitOverlappingToday } from './clientDetailsV2.utils';

interface ClientDetailsV2HeaderProps {
  clientId: string;
  client?: Client;
  refreshClientData?: () => Promise<void>;
  updateLocalClient?: (updater: (currentClient: Client) => Client) => void;
  clientDisplayName: string;
  emailDisplay: string;
  aboutDetails: AboutDetails;
  allergies: string[];
  intolerances: string[];
  conditionNames: string[];
  summaryItems: SummaryItem[];
  metrics: MetricItem[];
  anthropometricItems?: DetailMetricItem[];
  activityAndGoalItems?: DetailMetricItem[];
  symptomItems?: DetailMetricItem[];
  dietaryPreferencesSummary?: string;
  clientGoalSummary?: string;
  nutrientGuidelinesSummary?: string;
  clientGoalRows?: ClientGoalRow[];
  completedQuestionnaires: number;
  questionnaireCompletedLabel: string;
  nextAppointmentDate: string | null;
  activeSubscriptionSummary?: ActiveSubscriptionSummary | null;
  statusLabel: string;
  activeTab: number;
  tabs: readonly string[];
  onTabChange: (value: number) => void;
  onOpenQuestionnaires: () => void;
  onUpdateAllergies?: (allergies: string[]) => void;
  onUpdateIntolerances?: (intolerances: string[]) => void;
  onUpdateMedicalConditions?: (medicalConditions: string[], medicalConditionSubtypes?: Record<string, string>) => void;
  onAddNote?: () => void;
  onDeleteClient?: () => void;
  clientGoalsEditorAnchorEl?: HTMLElement | null;
  onCloseClientGoalsEditor?: () => void;
  hideTabs?: boolean;
}

const cardHeight = 64;
const getDefaultAllergySuggestions = () => getValidAllergies().map(a => a.value);
const getDefaultIntoleranceSuggestions = () => getValidFoodIntolerances().map(i => i.value);
const getDefaultCuisineSuggestions = () => getValidCuisineTypes().map((cuisine) => cuisine.value);
const getDefaultPreferenceSuggestions = () => getValidPreferences().map((preference) => preference.value);
const defaultMedicalConditionSuggestions = MEDICAL_CONDITIONS_API_OPTIONS.map((condition) => condition.id);
const generalWeightGoalOptions = GENERAL_WEIGHT_GOAL_OPTIONS;
const lactationWeightGoalOptions = WEIGHT_LOSS_GOAL_OPTIONS;
const diabetesSubtypeShortcutLabels: Record<string, string> = {
  'Type 1': 'T1D',
  'Type 2': 'T2D',
  LADA: 'LADA',
  Gestational: 'GDM',
  Prediabetes: 'PreD',
  'Not sure': 'Not sure',
};
const thyroidSubtypeShortcutLabels: Record<string, string> = {
  'Underactive (hypothyroid)': 'Hypo',
  'Overactive (hyperthyroid)': 'Hyper',
  "Hashimoto's": 'Hashi',
  Graves: 'Graves',
  'Not sure': 'Not sure',
};
const medicalConditionSubtypeOptionsByCondition: Record<string, Array<{ value: string; label: string; shortcut: string }>> = {
  diabetes: DIABETES_TYPE_OPTIONS.map((option) => ({
    ...option,
    shortcut: diabetesSubtypeShortcutLabels[option.value] || option.label,
  })),
  thyroid: THYROID_TYPE_OPTIONS.map((option) => ({
    ...option,
    shortcut: thyroidSubtypeShortcutLabels[option.value] || option.label,
  })),
};
const bluePanelGlassSx = {
  // Avoid backdrop blur here: the dashboard sidebar intentionally resizes on hover,
  // and Chrome can flash/repaint blurred translucent layers during that layout change.
  backgroundClip: 'padding-box',
} as const;

const clientGoalRowNutrientMap: Readonly<Record<string, { nutrient: MacroGoalNutrient; kcalPerGram: number }>> = {
  Protein: { nutrient: 'protein', kcalPerGram: 4 },
  Carbohydrate: { nutrient: 'carbohydrates', kcalPerGram: 4 },
  'Total Fat': { nutrient: 'totalFat', kcalPerGram: 9 },
  Fiber: { nutrient: 'dietaryFibre', kcalPerGram: 2 },
};

const calculateDerivedEer = (rows: ClientGoalRow[]): number | null => {
  let hasMacroValue = false;
  let totalCalories = 0;

  for (const row of rows) {
    const config = clientGoalRowNutrientMap[row.nutrient];
    // Fibre is a separate intake target. Protein, carbohydrate, and total fat
    // already allocate 100% of the daily energy target, so adding fibre here
    // would count it twice.
    if (!config || row.nutrient === 'Fiber' || row.grams.trim() === '') continue;

    const kcal = row.kcal == null || row.kcal.trim() === '' ? Number.NaN : Number(row.kcal);
    if (Number.isFinite(kcal) && kcal >= 0) {
      hasMacroValue = true;
      totalCalories += kcal;
      continue;
    }

    const grams = Number(row.grams);
    if (!Number.isFinite(grams) || grams < 0) continue;
    hasMacroValue = true;
    totalCalories += grams * config.kcalPerGram;
  }

  return hasMacroValue ? Math.round(totalCalories) : null;
};

const calculateDerivedEnergyPercentage = (
  row: ClientGoalRow,
  totalCalories: number | null,
): string | null => {
  const config = clientGoalRowNutrientMap[row.nutrient];
  if (!config || row.nutrient === 'Fiber' || totalCalories === null || totalCalories <= 0 || row.grams.trim() === '') return null;

  const kcal = row.kcal == null || row.kcal.trim() === '' ? Number.NaN : Number(row.kcal);
  if (Number.isFinite(kcal) && kcal >= 0) {
    return `${((kcal / totalCalories) * 100).toFixed(1)}%`;
  }

  const grams = Number(row.grams);
  if (!Number.isFinite(grams) || grams < 0) return null;

  return `${((grams * config.kcalPerGram / totalCalories) * 100).toFixed(1)}%`;
};

function getCompletedQuestionnaireCount(questionnaires: QuestionnaireInstanceResponse[] | undefined): number | null {
  if (!Array.isArray(questionnaires)) return null;
  return questionnaires.filter((questionnaire) => questionnaire.status === 'completed').length;
}

function getQuestionnaireCompletedLabel(completedQuestionnaires: number): string {
  return completedQuestionnaires === 1 ? '1 Complete' : `${completedQuestionnaires} Completed`;
}

const anthropometricEditFieldSx = {
  '& .MuiInputLabel-root': {
    fontFamily: 'Inter, sans-serif',
    color: '#667085',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#128fbd',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#fff',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    color: '#1D2B2D',
    transition: 'box-shadow 0.18s ease, background-color 0.18s ease',
    '& fieldset': {
      borderColor: '#d9e4ea',
    },
    '&:hover fieldset': {
      borderColor: '#8edcef',
    },
    '&.Mui-focused': {
      boxShadow: 'none',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#20AEDC',
      borderWidth: 1,
    },
  },
  '& .MuiInputLabel-shrink': {
    transform: 'translate(14px, -8px) scale(0.75)',
    bgcolor: '#fff',
    px: '1px',
    lineHeight: 1.2,
  },
  '& .MuiOutlinedInput-notchedOutline legend > span': {
    paddingLeft: 0,
    paddingRight: 0,
  },
  '& .MuiSelect-icon': {
    color: '#667085',
  },
} as const;

const AllergiesGlyph = ({ size = 16 }: { size?: number }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ display: 'block' }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.3333 3.66732C13.3333 4.2196 12.8856 4.66732 12.3333 4.66732C12.2438 4.66732 12.1569 4.65554 12.0743 4.63345L11.6535 5.05422C11.6383 5.06948 11.6223 5.08354 11.6057 5.0964C12.0523 5.64641 12.3789 6.2973 12.5443 7.00806C12.5844 7.00317 12.6252 7.00065 12.6667 7.00065C13.219 7.00065 13.6667 7.44837 13.6667 8.00065C13.6667 8.55294 13.219 9.00065 12.6667 9.00065H12.5748C12.3947 9.89158 11.9622 10.6833 11.3498 11.3034L12.1075 12.0677C12.1901 12.0456 12.277 12.0339 12.3665 12.0339C12.9188 12.0339 13.3665 12.4816 13.3665 13.0339C13.3665 13.5861 12.9188 14.0339 12.3665 14.0339C11.8143 14.0339 11.3665 13.5861 11.3665 13.0339C11.3665 12.9443 11.3783 12.8574 11.4004 12.7748L10.6464 12.0209C10.6194 11.9938 10.5961 11.9644 10.5765 11.9333C10.2621 12.1368 9.92085 12.3028 9.55899 12.4251L9.41454 12.8339C9.20887 13.4158 8.65391 13.834 8 13.834C7.34609 13.834 6.79113 13.4158 6.58546 12.8339L6.42053 12.3672C6.0906 12.2432 5.77782 12.0834 5.48699 11.8925L4.56934 12.833C4.6321 12.9876 4.66667 13.1568 4.66667 13.334C4.66667 14.0704 4.06971 14.6673 3.33333 14.6673C2.59695 14.6673 2 14.0704 2 13.334C2 12.5976 2.59695 12.0007 3.33333 12.0007C3.5255 12.0007 3.70818 12.0413 3.87323 12.1145L4.71006 11.2568C4.17787 10.7193 3.77235 10.0562 3.54354 9.31751C3.47506 9.32835 3.40486 9.33398 3.33333 9.33398C2.59695 9.33398 2 8.73703 2 8.00065C2 7.26427 2.59695 6.66732 3.33333 6.66732C3.3907 6.66732 3.44723 6.67094 3.50269 6.67797C3.671 6.07417 3.95886 5.52164 4.33948 5.04716L3.8541 4.56178C3.69411 4.62973 3.51811 4.66732 3.33333 4.66732C2.59695 4.66732 2 4.07036 2 3.33398C2 2.5976 2.59695 2.00065 3.33333 2.00065C4.06971 2.00065 4.66667 2.5976 4.66667 3.33398C4.66667 3.51872 4.6291 3.69468 4.56118 3.85465L5.04659 4.34006C5.72938 3.79237 6.57378 3.43677 7.5 3.35304V3.2002C7.2011 3.0273 7 2.70413 7 2.33398C7 1.7817 7.44772 1.33398 8 1.33398C8.55228 1.33398 9 1.7817 9 2.33398C9 2.70413 8.7989 3.0273 8.5 3.2002V3.36909C9.40278 3.47978 10.2294 3.847 10.9042 4.39498C10.9171 4.37836 10.9312 4.36237 10.9464 4.34712L11.3672 3.92634C11.3451 3.84373 11.3333 3.7569 11.3333 3.66732C11.3333 3.11503 11.781 2.66732 12.3333 2.66732C12.8856 2.66732 13.3333 3.11503 13.3333 3.66732ZM7.83333 8.66732C7.83333 7.6548 8.65414 6.83398 9.66667 6.83398C10.6792 6.83398 11.5 7.6548 11.5 8.66732C11.5 9.67984 10.6792 10.5007 9.66667 10.5007C8.65414 10.5007 7.83333 9.67984 7.83333 8.66732ZM7 6.66732C7.55229 6.66732 8 6.2196 8 5.66732C8 5.62864 7.9978 5.59048 7.99353 5.55294C7.9569 5.23128 7.76767 4.95594 7.5 4.8011C7.35291 4.71601 7.18214 4.66732 7 4.66732C6.44772 4.66732 6 5.11503 6 5.66732C6 6.2196 6.44772 6.66732 7 6.66732ZM6 9.33398C6.36819 9.33398 6.66667 9.03551 6.66667 8.66732C6.66667 8.29913 6.36819 8.00065 6 8.00065C5.63181 8.00065 5.33333 8.29913 5.33333 8.66732C5.33333 9.03551 5.63181 9.33398 6 9.33398Z"
      fill="#20AEDC"
    />
    <path
      d="M8.83333 8.66732C8.83333 8.20708 9.20643 7.83398 9.66667 7.83398C10.1269 7.83398 10.5 8.20708 10.5 8.66732C10.5 9.12755 10.1269 9.50065 9.66667 9.50065C9.20643 9.50065 8.83333 9.12755 8.83333 8.66732Z"
      fill="#20AEDC"
    />
  </Box>
);

const MedicalConditionsGlyph = ({ size = 16 }: { size?: number }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ display: 'block' }}
  >
    <path
      opacity="0.5"
      d="M2 6.66732C2 4.15316 2 2.89608 2.78105 2.11503C3.5621 1.33398 4.81918 1.33398 7.33333 1.33398H8.66667C11.1808 1.33398 12.4379 1.33398 13.219 2.11503C14 2.89608 14 4.15316 14 6.66732V9.33398C14 11.8481 14 13.1052 13.219 13.8863C12.4379 14.6673 11.1808 14.6673 8.66667 14.6673H7.33333C4.81918 14.6673 3.5621 14.6673 2.78105 13.8863C2 13.1052 2 11.8481 2 9.33398V6.66732Z"
      fill="#20AEDC"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.00003 3.5C8.27617 3.5 8.50003 3.72386 8.50003 4V4.83333H9.33335C9.6095 4.83333 9.83335 5.05719 9.83335 5.33333C9.83335 5.60948 9.6095 5.83333 9.33335 5.83333L8.50003 5.83333L8.50003 6.66667C8.50003 6.94281 8.27617 7.16667 8.00003 7.16667C7.72389 7.16667 7.50003 6.94281 7.50003 6.66667L7.50003 5.83333H6.66669C6.39054 5.83333 6.16669 5.60948 6.16669 5.33333C6.16669 5.05719 6.39054 4.83333 6.66669 4.83333H7.50003V4C7.50003 3.72386 7.72389 3.5 8.00003 3.5ZM4.83337 9.33333C4.83337 9.05719 5.05723 8.83333 5.33337 8.83333H10.6667C10.9428 8.83333 11.1667 9.05719 11.1667 9.33333C11.1667 9.60948 10.9428 9.83333 10.6667 9.83333H5.33337C5.05723 9.83333 4.83337 9.60948 4.83337 9.33333ZM5.50004 12C5.50004 11.7239 5.7239 11.5 6.00004 11.5H10C10.2762 11.5 10.5 11.7239 10.5 12C10.5 12.2761 10.2762 12.5 10 12.5H6.00004C5.7239 12.5 5.50004 12.2761 5.50004 12Z"
      fill="#20AEDC"
    />
  </Box>
);

const IntolerancesGlyph = ({ size = 16 }: { size?: number }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ display: 'block' }}
  >
    <path
      opacity="0.5"
      d="M9.83539 4.75015C10.6164 5.5312 11.8828 5.5312 12.6638 4.75015C13.0543 4.35963 13.2496 3.84778 13.2496 3.33594C13.2496 2.82409 13.0543 2.31225 12.6638 1.92172C11.8828 1.14068 10.6164 1.14068 9.83539 1.92172C9.44487 2.31225 9.2496 2.82409 9.2496 3.33594C9.2496 3.84778 9.44487 4.35963 9.83539 4.75015Z"
      fill="#20AEDC"
    />
    <path
      opacity="0.5"
      d="M13.9339 11.59C14.9071 10.6168 14.9071 9.03898 13.9339 8.06581C12.9608 7.09265 11.383 7.09265 10.4098 8.06581L8.06036 10.4152C7.08719 11.3884 7.08719 12.9662 8.06036 13.9394C9.03353 14.9126 10.6113 14.9126 11.5845 13.9394L13.9339 11.59Z"
      fill="#20AEDC"
    />
    <path
      d="M13.2504 12.2745L13.2507 12.2725L12.7628 12.1785C12.76 12.1779 12.7531 12.1764 12.7436 12.1739C12.7244 12.1691 12.6921 12.1604 12.6483 12.1464C12.5606 12.1184 12.4269 12.0694 12.26 11.989C11.9268 11.8284 11.4593 11.5414 10.9594 11.0415C10.4595 10.5416 10.1725 10.0741 10.0119 9.74084C9.93141 9.57391 9.88246 9.44019 9.85444 9.35248C9.84043 9.30863 9.82721 9.25771 9.82238 9.23856L9.72819 8.75L9.72621 8.75038L8.88257 9.59403C8.88829 9.61341 8.8947 9.63434 8.90185 9.65674C8.94296 9.78543 9.00879 9.96287 9.11103 10.175C9.3158 10.5999 9.66575 11.1621 10.2523 11.7486C10.8389 12.3352 11.4011 12.6851 11.8259 12.8899C12.038 12.9921 12.2154 13.0579 12.3441 13.099C12.3665 13.1061 12.3873 13.1125 12.4067 13.1182L13.2504 12.2745Z"
      fill="#20AEDC"
    />
    <path
      d="M6.48717 8.45726C5.29908 9.62883 3.3728 9.62883 2.1847 8.45726C1.7201 7.99911 1.43717 7.42965 1.33594 6.83594H7.33594C7.2347 7.42965 6.95178 7.99911 6.48717 8.45726Z"
      fill="#20AEDC"
    />
    <path
      d="M7.33594 5.83594H1.33594C1.43717 5.24222 1.7201 4.67276 2.1847 4.21462C3.3728 3.04304 5.29908 3.04304 6.48717 4.21462C6.95178 4.67276 7.2347 5.24222 7.33594 5.83594Z"
      fill="#20AEDC"
    />
  </Box>
);

const CurrentStatusGlyph = ({ size = 20 }: { size?: number }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ display: 'block' }}
  >
    <path
      opacity="0.5"
      d="M10 20C5.28595 20 2.92893 20 1.46447 18.5355C0 17.0711 0 14.714 0 10C0 5.28595 0 2.92893 1.46447 1.46447C2.92893 0 5.28595 0 10 0C14.714 0 17.0711 0 18.5355 1.46447C20 2.92893 20 5.28595 20 10C20 14.714 20 17.0711 18.5355 18.5355C17.0711 20 14.714 20 10 20Z"
      fill="#20AEDC"
    />
  </Box>
);

const AddToNotesGlyph = ({ size = 14 }: { size?: number }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ display: 'block' }}
  >
    <g clipPath="url(#add-to-notes-clip)">
      <path
        d="M5.79309 9.04702C5.74101 8.84515 5.63579 8.66091 5.48837 8.51349C5.34095 8.36607 5.15671 8.26085 4.95484 8.20877L1.37609 7.28594C1.31503 7.26861 1.26129 7.23183 1.22303 7.1812C1.18477 7.13056 1.16406 7.06882 1.16406 7.00535C1.16406 6.94188 1.18477 6.88015 1.22303 6.82951C1.26129 6.77887 1.31503 6.7421 1.37609 6.72477L4.95484 5.80135C5.15664 5.74932 5.34083 5.64419 5.48824 5.49688C5.63566 5.34956 5.74092 5.16545 5.79309 4.96369L6.71592 1.38494C6.73308 1.32364 6.76981 1.26964 6.82053 1.23117C6.87124 1.1927 6.93314 1.17188 6.9968 1.17188C7.06045 1.17188 7.12236 1.1927 7.17307 1.23117C7.22378 1.26964 7.26052 1.32364 7.27767 1.38494L8.19992 4.96369C8.252 5.16556 8.35723 5.34979 8.50465 5.49721C8.65207 5.64463 8.8363 5.74986 9.03817 5.80194L12.6169 6.72419C12.6785 6.74116 12.7327 6.77786 12.7714 6.82865C12.8101 6.87944 12.831 6.94151 12.831 7.00535C12.831 7.06919 12.8101 7.13127 12.7714 7.18206C12.7327 7.23285 12.6785 7.26955 12.6169 7.28652L9.03817 8.20877C8.8363 8.26085 8.65207 8.36607 8.50465 8.51349C8.35723 8.66091 8.252 8.84515 8.19992 9.04702L7.27709 12.6258C7.25993 12.6871 7.2232 12.7411 7.17249 12.7795C7.12177 12.818 7.05987 12.8388 6.99621 12.8388C6.93256 12.8388 6.87066 12.818 6.81994 12.7795C6.76923 12.7411 6.73249 12.6871 6.71534 12.6258L5.79309 9.04702Z"
        stroke="#0A0A0A"
        strokeWidth="1.16667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M11.6641 1.75V4.08333" stroke="#0A0A0A" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12.8333 2.92188H10.5" stroke="#0A0A0A" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.33594 9.92188V11.0885" stroke="#0A0A0A" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.91667 10.5H1.75" stroke="#0A0A0A" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="add-to-notes-clip">
        <rect width="14" height="14" fill="white" />
      </clipPath>
    </defs>
  </Box>
);

function toUniqueLabelList(values: string[] | undefined | null): string[] {
  if (!Array.isArray(values)) return [];

  const seen = new Set<string>();
  const result: string[] = [];

  values.forEach((value) => {
    const normalizedValue = value.trim();
    if (!normalizedValue) return;

    // Convert display name to value if it exists in constants
    let finalValue = normalizedValue;
    const foundAllergy = getValidAllergies().find(a => a.name.toLowerCase() === normalizedValue.toLowerCase());
    if (foundAllergy) finalValue = foundAllergy.value;
    
    const foundIntolerance = getValidFoodIntolerances().find(i => i.name.toLowerCase() === normalizedValue.toLowerCase());
    if (foundIntolerance) finalValue = foundIntolerance.value;

    const foundCuisine = getValidCuisineTypes().find(c => c.name.toLowerCase() === normalizedValue.toLowerCase());
    if (foundCuisine) finalValue = foundCuisine.value;

    const foundPreference = getValidPreferences().find(p => p.name.toLowerCase() === normalizedValue.toLowerCase());
    if (foundPreference) finalValue = foundPreference.value;

    const mappedMedicalCondition = mapMedicalConditionToApiValue(normalizedValue);
    if (mappedMedicalCondition) finalValue = mappedMedicalCondition;

    const key = finalValue.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(finalValue);
  });

  return result;
}

function toTitleDisplayLabel(value: string): string {
  const normalized = value.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  if (!normalized) return value;

  return normalized
    .split(' ')
    .map((word) => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}` : word))
    .join(' ');
}

function formatShortDisplayList(values: string[], emptyLabel: string, resolveLabel: (value: string) => string): string {
  const labels = values.map(resolveLabel).filter(Boolean);
  if (!labels.length) return emptyLabel;
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
}

function getMedicalConditionSubtypeOptions(value: string) {
  const conditionId = mapMedicalConditionToApiValue(value).toLowerCase();
  return medicalConditionSubtypeOptionsByCondition[conditionId] || [];
}

function normalizeSubtypeToken(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeMedicalConditionSubtypeValue(conditionId: string, subtype: unknown): string {
  const value = String(subtype ?? '').trim();
  if (!value) return '';

  const options = medicalConditionSubtypeOptionsByCondition[conditionId] || [];
  const normalizedValue = normalizeSubtypeToken(value);
  const matched = options.find((option) => {
    return (
      normalizeSubtypeToken(option.value) === normalizedValue ||
      normalizeSubtypeToken(option.label) === normalizedValue ||
      normalizeSubtypeToken(option.shortcut) === normalizedValue
    );
  });

  return matched?.value || '';
}

function isUnknownMedicalConditionSubtype(subtype: unknown): boolean {
  return normalizeSubtypeToken(subtype) === 'not sure';
}

function getMedicalConditionSubtypeShortcut(conditionId: string, subtype: string): string {
  const normalizedValue = normalizeMedicalConditionSubtypeValue(conditionId, subtype);
  if (!normalizedValue || isUnknownMedicalConditionSubtype(normalizedValue)) return 'Set';
  const option = medicalConditionSubtypeOptionsByCondition[conditionId]?.find(
    (item) => item.value.toLowerCase() === normalizedValue.toLowerCase(),
  );
  return option?.shortcut || normalizedValue;
}

function getMedicalConditionSubtypeLabel(conditionId: string, subtype: string): string {
  const normalizedValue = normalizeMedicalConditionSubtypeValue(conditionId, subtype);
  if (!normalizedValue || isUnknownMedicalConditionSubtype(normalizedValue)) return 'Select subtype';
  const option = medicalConditionSubtypeOptionsByCondition[conditionId]?.find(
    (item) => item.value.toLowerCase() === normalizedValue.toLowerCase(),
  );
  return option?.label || normalizedValue;
}

function buildMedicalConditionSubtypeMapFromClient(client: Client | null | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  if (!Array.isArray(client?.medicalConditionDetails)) return result;

  client.medicalConditionDetails.forEach((condition) => {
    if (!condition || !condition.conditionSubtype) return;
    if (String(condition.status ?? '').trim().toLowerCase() === 'resolved') return;

    const conditionId = mapMedicalConditionToApiValue(
      String(condition.conditionId || condition.conditionName || '').trim(),
    ).toLowerCase();
    if (!medicalConditionSubtypeOptionsByCondition[conditionId]) return;

    result[conditionId] = normalizeMedicalConditionSubtypeValue(conditionId, condition.conditionSubtype);
  });

  return result;
}

function buildSelectedMedicalConditionSubtypePayload(
  medicalConditions: string[],
  subtypeMap: Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = {};
  medicalConditions.forEach((condition) => {
    const conditionId = mapMedicalConditionToApiValue(condition).toLowerCase();
    if (!medicalConditionSubtypeOptionsByCondition[conditionId]) return;
    result[conditionId] = normalizeMedicalConditionSubtypeValue(conditionId, subtypeMap[conditionId] ?? '');
  });
  return result;
}

function areRecordValuesEqual(left: Record<string, string>, right: Record<string, string>): boolean {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every((key) => String(left[key] ?? '') === String(right[key] ?? ''));
}

function getBiologicalSexForCalculations(client?: Client): 'male' | 'female' | '' {
  const gender = String(client?.gender || '').trim().toLowerCase();
  if (gender === 'male' || gender === 'female') return gender;

  const sexAtBirth = String(client?.sexAtBirth || '').trim().toLowerCase();
  if (sexAtBirth === 'male' || sexAtBirth === 'female') return sexAtBirth;

  return '';
}

function normalizeSexAtBirthForSave(value: unknown): 'male' | 'female' | undefined {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'male' || normalized === 'female' ? normalized : undefined;
}

function getGrowthFactorEnabled(client?: Client): boolean | null {
  if (typeof client?.applyGrowthFactor === 'boolean') {
    return client.applyGrowthFactor;
  }

  if (typeof client?.calculationDetails?.growthFactorApplied === 'boolean') {
    return client.calculationDetails.growthFactorApplied;
  }

  return null;
}

const normalizeActivityToken = (value: string): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[()]/g, '')
    .replace(/[\s-]+/g, '_');

const mapActivityLevelForSave = (value: string): string => {
  const token = normalizeActivityToken(value);
  if (!token) return '';

  if (token.includes('athlete')) {
    if (token.includes('boy') || token.includes('male')) return 'athlete_boys';
    if (token.includes('girl') || token.includes('female')) return 'athlete_girls';
  }

  if (token.includes('sedentary')) return 'sedentary';
  if (token.includes('lightly_active') || token.includes('low_active')) return 'lightly_active';
  if (token.includes('moderately_active')) return 'moderately_active';
  if (token === 'moderate') return 'moderate';
  if (token === 'active' || token.startsWith('active_')) return 'active';
  if (token.includes('very_active')) return 'very_active';
  if (token.includes('extremely_active')) return 'extremely_active';
  if (token.includes('extra_active')) return 'extremely_active';

  return String(value).trim();
};

export const ClientDetailsV2Header: React.FC<ClientDetailsV2HeaderProps> = ({
  clientId,
  client,
  refreshClientData,
  updateLocalClient,
  clientDisplayName,
  emailDisplay,
  aboutDetails = {
    fullName: EMPTY_CLIENT_DETAIL_VALUE,
    dateOfBirth: EMPTY_CLIENT_DETAIL_VALUE,
    gender: EMPTY_CLIENT_DETAIL_VALUE,
    sexAtBirth: EMPTY_CLIENT_DETAIL_VALUE,
    phone: EMPTY_CLIENT_DETAIL_VALUE,
    email: EMPTY_CLIENT_DETAIL_VALUE,
    location: EMPTY_CLIENT_DETAIL_VALUE,
    cityOrCounty: EMPTY_CLIENT_DETAIL_VALUE,
    occupation: EMPTY_CLIENT_DETAIL_VALUE,
    emergencyContact: EMPTY_CLIENT_DETAIL_VALUE,
  },
  allergies = [],
  intolerances = [],
  conditionNames = [],
  summaryItems,
  metrics,
  anthropometricItems = [],
  activityAndGoalItems = [],
  symptomItems = [],
  dietaryPreferencesSummary = EMPTY_CLIENT_DETAIL_VALUE,
  nutrientGuidelinesSummary = 'DRI Formula- EFSA EU',
  clientGoalRows = [],
  completedQuestionnaires,
  questionnaireCompletedLabel,
  nextAppointmentDate,
  activeSubscriptionSummary = null,
  statusLabel,
  activeTab,
  tabs,
  onTabChange,
  onOpenQuestionnaires,
  onUpdateAllergies,
  onUpdateIntolerances,
  onUpdateMedicalConditions,
  onAddNote,
  // onDeleteClient,
  clientGoalsEditorAnchorEl = null,
  onCloseClientGoalsEditor,
  hideTabs = false,
}) => {
  const [currentStatusAnchorEl, setCurrentStatusAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [isAnthropometricExpanded, setIsAnthropometricExpanded] = React.useState(false);
  const [internalClientGoalsAnchorEl, setInternalClientGoalsAnchorEl] = React.useState<HTMLElement | null>(null);
  const [editableClientGoalRows, setEditableClientGoalRows] = React.useState<ClientGoalRow[]>([]);
  const [isSavingClientGoals, setIsSavingClientGoals] = React.useState(false);
  const [hasEditedEnergyMacros, setHasEditedEnergyMacros] = React.useState(false);
  const macroDerivedDailyCalorieGoal = React.useMemo(
    () => calculateDerivedEer(editableClientGoalRows),
    [editableClientGoalRows],
  );
  const savedDailyCalorieGoal = React.useMemo(() => {
    const value = resolveClientTargetCalories(client);
    return value !== undefined && Number.isFinite(value) && value > 0 ? Math.round(value) : null;
  }, [client]);
  const derivedDailyCalorieGoal = hasEditedEnergyMacros
    ? macroDerivedDailyCalorieGoal
    : savedDailyCalorieGoal ?? macroDerivedDailyCalorieGoal;
  const nutritionistId = React.useMemo(() => getCurrentUser()?.id ?? undefined, []);
  const shouldLoadHeaderOngoingVisit = activeTab === 0 || activeTab === 1;
  const { data: questionnaireResponsesData } = useQuery({
    queryKey: ['questionnaireResponses', clientId, nutritionistId],
    queryFn: () => getQuestionnaireResponses(clientId, nutritionistId),
    enabled: Boolean(clientId),
  });
  const { data: ongoingVisitLive } = useQuery({
    queryKey: ['clientHeaderOngoingVisitToday', clientId],
    queryFn: async () => {
      const { appointments } = await getAppointments({ clientId, limit: 50 });
      return findOngoingVisitOverlappingToday(appointments as unknown[]);
    },
    enabled: Boolean(clientId) && shouldLoadHeaderOngoingVisit,
    staleTime: 15_000,
    refetchInterval: shouldLoadHeaderOngoingVisit ? 60_000 : false,
  });
  const ongoingVisitBanner = shouldLoadHeaderOngoingVisit ? ongoingVisitLive : null;
  const completedQuestionnaireCountFromResponses = getCompletedQuestionnaireCount(questionnaireResponsesData?.questionnaires);
  const questionnairePanelCompletedCount = completedQuestionnaireCountFromResponses ?? completedQuestionnaires;
  const questionnairePanelCompletedLabel =
    completedQuestionnaireCountFromResponses == null
      ? questionnaireCompletedLabel
      : getQuestionnaireCompletedLabel(questionnairePanelCompletedCount);
  const [aboutAnchorEl, setAboutAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [isEditingAbout, setIsEditingAbout] = React.useState(false);
  const [isSavingAbout, setIsSavingAbout] = React.useState(false);
  const [aboutEditData, setAboutEditData] = React.useState({
    fullName: '',
    dateOfBirth: '',
    gender: '',
    sexAtBirth: '',
    phoneCountryCode: '',
    phone: '',
    email: '',
    location: '',
    cityOrCounty: '',
  });
  const [aboutPhoneCountry, setAboutPhoneCountry] = React.useState('in');
  const prevIsAboutPopoverOpenRef = React.useRef(false);
  const [allergiesAnchorEl, setAllergiesAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [intolerancesAnchorEl, setIntolerancesAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [dietaryPreferencesAnchorEl, setDietaryPreferencesAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [dietaryPreferencesPopoverMode, setDietaryPreferencesPopoverMode] = React.useState<'cuisine' | 'preferences'>('cuisine');
  const [medicalConditionsAnchorEl, setMedicalConditionsAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [allergySearchValue, setAllergySearchValue] = React.useState('');
  const [intoleranceSearchValue, setIntoleranceSearchValue] = React.useState('');
  const [medicalConditionSearchValue, setMedicalConditionSearchValue] = React.useState('');
  const [cuisineSearchValue, setCuisineSearchValue] = React.useState('');
  const [preferenceSearchValue, setPreferenceSearchValue] = React.useState('');
  const [selectedAllergies, setSelectedAllergies] = React.useState<string[]>(() => toUniqueLabelList(allergies));
  const [selectedIntolerances, setSelectedIntolerances] = React.useState<string[]>(() => toUniqueLabelList(intolerances));
  const [selectedMedicalConditions, setSelectedMedicalConditions] = React.useState<string[]>(() =>
    toUniqueLabelList(getEditableMedicalConditionValues(client)),
  );
  const [selectedMedicalConditionSubtypes, setSelectedMedicalConditionSubtypes] = React.useState<Record<string, string>>(
    () => buildMedicalConditionSubtypeMapFromClient(client),
  );
  const [selectedCuisineTypes, setSelectedCuisineTypes] = React.useState<string[]>(() => toUniqueLabelList(client?.cuisineTypesPreferred));
  const [selectedDietaryPreferences, setSelectedDietaryPreferences] = React.useState<string[]>(() => toUniqueLabelList(client?.dietaryPreferences));
  const [anthropometricEditAnchorEl, setAnthropometricEditAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [nutrientGuidelinesAnchorEl, setNutrientGuidelinesAnchorEl] = React.useState<HTMLElement | null>(null);
  const [nutrientGuidelineValue, setNutrientGuidelineValue] = React.useState('');
  const [isSavingNutrientGuideline, setIsSavingNutrientGuideline] = React.useState(false);
  const [isSavingAnthropometricData, setIsSavingAnthropometricData] = React.useState(false);
  const [anthropometricEditData, setAnthropometricEditData] = React.useState({
    weightKg: '',
    heightCm: '',
    waistCircumferenceCm: '',
    hipCircumferenceCm: '',
    activityLevel: '',
    weightGoal: '',
    healthGoal: '',
    pregnancyStatus: 'none',
    pregnancyTrimester: 'none',
    prePregnancyWeightKg: '',
    lactationPeriod: 'none',
    isTwinPregnancy: false,
    applyGrowthFactor: true,
    formulaId: '',
  });
  const [availableCalculationFormulas, setAvailableCalculationFormulas] = React.useState<CalculationFormula[]>([]);
  const [viableCalculationFormulas, setViableCalculationFormulas] = React.useState<CalculationFormula[]>([]);
  const [availableDrvGuidelines, setAvailableDrvGuidelines] = React.useState<DRVGuideline[]>([]);
  const [allowedActivityLevels, setAllowedActivityLevels] = React.useState<ResolvedActivityLevels | null>(null);
  const [isLoadingFormulaOptions, setIsLoadingFormulaOptions] = React.useState(false);
  const [showFormulaValidationModal, setShowFormulaValidationModal] = React.useState(false);
  const [nonViableFormulaName, setNonViableFormulaName] = React.useState('');
  const [formulaValidationFallbackName, setFormulaValidationFallbackName] = React.useState('a recommended formula');

  React.useEffect(() => {
    setSelectedAllergies(toUniqueLabelList(allergies));
  }, [allergies]);

  React.useEffect(() => {
    setSelectedIntolerances(toUniqueLabelList(intolerances));
  }, [intolerances]);

  React.useEffect(() => {
    setSelectedMedicalConditions(toUniqueLabelList(getEditableMedicalConditionValues(client)));
    setSelectedMedicalConditionSubtypes(buildMedicalConditionSubtypeMapFromClient(client));
  }, [client?.medicalConditionDetails, client?.medicalConditions]);

  React.useEffect(() => {
    setSelectedCuisineTypes(toUniqueLabelList(client?.cuisineTypesPreferred));
    setSelectedDietaryPreferences(toUniqueLabelList(client?.dietaryPreferences));
  }, [client?.cuisineTypesPreferred, client?.dietaryPreferences]);

  const isCurrentStatusPopoverOpen = Boolean(currentStatusAnchorEl);
  const activeClientGoalsAnchorEl = clientGoalsEditorAnchorEl ?? internalClientGoalsAnchorEl;
  const isClientGoalsOpen = Boolean(activeClientGoalsAnchorEl);
  const isAboutPopoverOpen = Boolean(aboutAnchorEl);
  const isAllergiesPopoverOpen = Boolean(allergiesAnchorEl);
  const isIntolerancesPopoverOpen = Boolean(intolerancesAnchorEl);
  const isDietaryPreferencesPopoverOpen = Boolean(dietaryPreferencesAnchorEl);
  const isMedicalConditionsPopoverOpen = Boolean(medicalConditionsAnchorEl);
  const isAnthropometricEditPopoverOpen = Boolean(anthropometricEditAnchorEl);
  const isNutrientGuidelinesPopoverOpen = Boolean(nutrientGuidelinesAnchorEl);

  const prevIsAllergiesPopoverOpen = React.useRef(isAllergiesPopoverOpen);
  React.useEffect(() => {
    if (prevIsAllergiesPopoverOpen.current && !isAllergiesPopoverOpen) {
      const original = toUniqueLabelList(allergies);
      const current = toUniqueLabelList(selectedAllergies);
      const hasChanged = original.length !== current.length || !original.every(val => current.includes(val));
      if (hasChanged) {
        onUpdateAllergies?.(current);
      }
    }
    prevIsAllergiesPopoverOpen.current = isAllergiesPopoverOpen;
  }, [isAllergiesPopoverOpen, allergies, selectedAllergies, onUpdateAllergies]);

  const prevIsIntolerancesPopoverOpen = React.useRef(isIntolerancesPopoverOpen);
  React.useEffect(() => {
    if (prevIsIntolerancesPopoverOpen.current && !isIntolerancesPopoverOpen) {
      const original = toUniqueLabelList(intolerances);
      const current = toUniqueLabelList(selectedIntolerances);
      const hasChanged = original.length !== current.length || !original.every(val => current.includes(val));
      if (hasChanged) {
        onUpdateIntolerances?.(current);
      }
    }
    prevIsIntolerancesPopoverOpen.current = isIntolerancesPopoverOpen;
  }, [isIntolerancesPopoverOpen, intolerances, selectedIntolerances, onUpdateIntolerances]);

  const originalMedicalConditions = React.useMemo(
    () => toUniqueLabelList(getEditableMedicalConditionValues(client)),
    [client?.medicalConditionDetails, client?.medicalConditions],
  );
  const originalMedicalConditionSubtypes = React.useMemo(
    () => buildMedicalConditionSubtypeMapFromClient(client),
    [client?.medicalConditionDetails],
  );
  const prevIsMedicalConditionsPopoverOpen = React.useRef(isMedicalConditionsPopoverOpen);
  React.useEffect(() => {
    if (prevIsMedicalConditionsPopoverOpen.current && !isMedicalConditionsPopoverOpen) {
      const original = mapMedicalConditionsToApiValues(originalMedicalConditions);
      const current = mapMedicalConditionsToApiValues(selectedMedicalConditions);
      const originalKeys = new Set(original.map((value) => value.toLowerCase()));
      const currentKeys = new Set(current.map((value) => value.toLowerCase()));
      const hasChanged =
        originalKeys.size !== currentKeys.size ||
        [...originalKeys].some((value) => !currentKeys.has(value));
      const originalSubtypes = buildSelectedMedicalConditionSubtypePayload(original, originalMedicalConditionSubtypes);
      const currentSubtypes = buildSelectedMedicalConditionSubtypePayload(current, selectedMedicalConditionSubtypes);
      const subtypesChanged = !areRecordValuesEqual(originalSubtypes, currentSubtypes);
      if (hasChanged || subtypesChanged) {
        onUpdateMedicalConditions?.(current, currentSubtypes);
      }
    }
    prevIsMedicalConditionsPopoverOpen.current = isMedicalConditionsPopoverOpen;
  }, [
    isMedicalConditionsPopoverOpen,
    onUpdateMedicalConditions,
    originalMedicalConditions,
    originalMedicalConditionSubtypes,
    selectedMedicalConditionSubtypes,
    selectedMedicalConditions,
  ]);

  const saveDietaryPreferencesIfChanged = React.useCallback(async () => {
    const originalCuisines = toUniqueLabelList(client?.cuisineTypesPreferred);
    const originalPreferences = toUniqueLabelList(client?.dietaryPreferences);
    const nextCuisines = toUniqueLabelList(selectedCuisineTypes);
    const nextPreferences = toUniqueLabelList(selectedDietaryPreferences);
    const cuisinesChanged = originalCuisines.length !== nextCuisines.length || !originalCuisines.every(val => nextCuisines.includes(val));
    const preferencesChanged = originalPreferences.length !== nextPreferences.length || !originalPreferences.every(val => nextPreferences.includes(val));

    if (!cuisinesChanged && !preferencesChanged) return;

    try {
      await updateClient(clientId, {
        cuisineTypesPreferred: nextCuisines,
        dietaryPreferences: nextPreferences,
      });
      await refreshClientData?.();
      const successMessage =
        cuisinesChanged && preferencesChanged
          ? 'Cuisine types and preferences updated successfully'
          : cuisinesChanged
            ? 'Cuisine types updated successfully'
            : 'Preferences updated successfully';
      showSuccessToast('Success', successMessage);
    } catch (error) {
      console.error('Failed to update dietary preferences:', error);
      showErrorToast(error, 'Failed to update dietary preferences');
    }
  }, [
    client?.cuisineTypesPreferred,
    client?.dietaryPreferences,
    clientId,
    refreshClientData,
    selectedCuisineTypes,
    selectedDietaryPreferences,
  ]);
  const currentStatusPopoverId = isCurrentStatusPopoverOpen ? 'client-details-current-status-popover' : undefined;
  const clientGoalsPopoverId = isClientGoalsOpen ? 'client-details-client-goals-popover' : undefined;
  const aboutPopoverId = isAboutPopoverOpen ? 'client-details-about-popover' : undefined;
  const allergiesPopoverId = isAllergiesPopoverOpen ? 'client-details-allergies-popover' : undefined;
  const intolerancesPopoverId = isIntolerancesPopoverOpen ? 'client-details-intolerances-popover' : undefined;
  const dietaryPreferencesPopoverId = isDietaryPreferencesPopoverOpen ? 'client-details-dietary-preferences-popover' : undefined;
  const medicalConditionsPopoverId = isMedicalConditionsPopoverOpen ? 'client-details-medical-conditions-popover' : undefined;
  const anthropometricEditPopoverId = isAnthropometricEditPopoverOpen ? 'client-details-anthropometric-edit-popover' : undefined;
  const nutrientGuidelinesPopoverId = isNutrientGuidelinesPopoverOpen ? 'client-details-nutrient-guidelines-popover' : undefined;
  const biologicalSexForCalculations = React.useMemo(() => getBiologicalSexForCalculations(client), [client]);
  const clientAge = React.useMemo(() => calculateAgeFromDateString(client?.dateOfBirth) ?? null, [client?.dateOfBirth]);
  const shouldShowPregnancyRelatedFields = biologicalSexForCalculations === 'female' && (clientAge ?? 0) >= 18;
  const isFemaleClient = biologicalSexForCalculations === 'female';
  const isAnthropometricEditPregnant = anthropometricEditData.pregnancyStatus === 'pregnant';
  const isAnthropometricEditLactating =
    isFemaleClient &&
    Boolean(anthropometricEditData.lactationPeriod) &&
    anthropometricEditData.lactationPeriod !== 'none';
  const showPregnancyTypeEditor = shouldShowPregnancyRelatedFields && (isAnthropometricEditPregnant || isAnthropometricEditLactating);
  const showLactationWeightGoalEditor =
    shouldShowPregnancyRelatedFields && isAnthropometricEditLactating;
  const showPrimaryWeightGoalEditor =
    (clientAge ?? 0) >= 18 &&
    !(shouldShowPregnancyRelatedFields && (isAnthropometricEditPregnant || isAnthropometricEditLactating));
  const currentCalculationFormulaId = client?.bmrFormulaId || client?.formulaId || client?.calculationDetails?.formulaUsed || '';
  const currentCalculationFormulaLabel =
    client?.calculationDetails?.formulaDisplayName ||
    currentCalculationFormulaId ||
    'Current calculation formula';
  const isCurrentIomFormula = React.useMemo(
    () => isIomFormulaSelection(currentCalculationFormulaId, currentCalculationFormulaLabel),
    [currentCalculationFormulaId, currentCalculationFormulaLabel],
  );
  const currentDrvFormulaId = client?.drvGuidelineId || client?.drvFormulaId || '';
  const clientAgeForFormulaLookup = clientAge ?? 30;
  const clientAgeInMonths = React.useMemo(
    () => calculateAgeInMonthsFromDateString(client?.dateOfBirth),
    [client?.dateOfBirth],
  );
  const isInfantForActivity = clientAgeInMonths !== null && clientAgeInMonths >= 0 && clientAgeInMonths < 12;
  const fallbackAllowedActivityLevels = React.useMemo(
    () =>
      resolveAllowedActivityLevels({
        age: clientAge,
        biologicalSex: biologicalSexForCalculations,
        backendAllowedActivityLevels: null,
      }),
    [biologicalSexForCalculations, clientAge],
  );
  const resolvedAllowedActivityLevels = allowedActivityLevels ?? fallbackAllowedActivityLevels;
  const activityLevelOptions = React.useMemo(
    () => getActivityLevelSelectOptions(resolvedAllowedActivityLevels),
    [resolvedAllowedActivityLevels],
  );
  const normalizedClientActivityLevel = React.useMemo(
    () => mapActivityLevelForSave(client?.activityLevel || ''),
    [client?.activityLevel],
  );
  const displayActivityLevelOption = React.useMemo(
    () => getActivityLevelOptionByValue(resolvedAllowedActivityLevels, normalizedClientActivityLevel),
    [normalizedClientActivityLevel, resolvedAllowedActivityLevels],
  );
  const selectedCalculationFormula = React.useMemo(
    () => availableCalculationFormulas.find((formula) => String(formula.id) === String(anthropometricEditData.formulaId)),
    [anthropometricEditData.formulaId, availableCalculationFormulas],
  );
  const selectedCalculationFormulaLabel =
    getFormulaDisplayName(selectedCalculationFormula) ||
    (String(anthropometricEditData.formulaId) === String(currentCalculationFormulaId) ? currentCalculationFormulaLabel : '');
  const isSelectedIomFormula = React.useMemo(
    () => isIomFormulaSelection(anthropometricEditData.formulaId, selectedCalculationFormulaLabel),
    [anthropometricEditData.formulaId, selectedCalculationFormulaLabel],
  );
  const isChildForGrowthFactor = clientAge !== null && clientAge <= 18;
  const shouldShowGrowthFactorDisplay = isChildForGrowthFactor && !isCurrentIomFormula;
  const shouldShowGrowthFactorEditor = isChildForGrowthFactor && !isSelectedIomFormula;
  const isActivityLevelRequired = (clientAge ?? 0) >= 1 && (!isSelectedIomFormula || (clientAge ?? 0) >= 3);
  const displayedActivityAndGoalItems = React.useMemo(() => {
    const growthFactorEnabled = getGrowthFactorEnabled(client);
    const items = activityAndGoalItems
      .filter((item) => !(isInfantForActivity && item.label === 'Physical Activity Level'))
      .map((item) => {
        if (item.label !== 'Physical Activity Level') return item;

        return {
          ...item,
          value: normalizedClientActivityLevel
            ? displayActivityLevelOption?.label || item.value
            : '-',
        };
      });

    if (!shouldShowGrowthFactorDisplay) return items;

    return [
      ...items,
      {
        label: 'Growth Factor',
        value: growthFactorEnabled == null ? '-' : growthFactorEnabled ? 'Enabled' : 'Disabled',
      },
    ];
  }, [activityAndGoalItems, client, displayActivityLevelOption?.label, isInfantForActivity, normalizedClientActivityLevel, shouldShowGrowthFactorDisplay]);

  const hydrateAboutEditData = React.useCallback(() => {
    const dateValue = client?.dateOfBirth ? new Date(client.dateOfBirth) : null;
    const locationPhoneCountry = client?.location ? getPhoneCountryFromLocation(client.location) : undefined;
    const dialPhoneCountry = client?.phoneCountryCode ? getCountryCodeFromDialCode(client.phoneCountryCode) : undefined;

    setAboutEditData({
      fullName: client?.fullName || clientDisplayName || '',
      dateOfBirth: dateValue && !Number.isNaN(dateValue.getTime()) ? dateValue.toISOString().slice(0, 10) : '',
      gender: client?.gender || '',
      sexAtBirth: client?.sexAtBirth || '',
      phoneCountryCode: client?.phoneCountryCode || '',
      phone: client?.phone || '',
      email: client?.email || '',
      location: client?.location || '',
      cityOrCounty: client?.cityOrCounty || '',
    });
    setAboutPhoneCountry(locationPhoneCountry || dialPhoneCountry || 'in');
  }, [client, clientDisplayName]);

  React.useEffect(() => {
    if (!isAboutPopoverOpen) {
      prevIsAboutPopoverOpenRef.current = false;
      return;
    }

    const justOpened = !prevIsAboutPopoverOpenRef.current;
    prevIsAboutPopoverOpenRef.current = true;

    if (justOpened) {
      hydrateAboutEditData();
      setIsEditingAbout(false);
      return;
    }

    if (!isEditingAbout) {
      hydrateAboutEditData();
    }
  }, [hydrateAboutEditData, isAboutPopoverOpen, isEditingAbout]);

  const hydrateAnthropometricEditData = React.useCallback(() => {
    const clientPregnancyTrimester = client?.pregnancyTrimester || 'none';
    const isClientPregnant = clientPregnancyTrimester !== 'none';
    const isClientLactating =
      isFemaleClient &&
      Boolean(client?.lactationPeriod) &&
      client?.lactationPeriod !== 'none';
    const clientWeightGoal = isClientLactating
      ? client?.lactationGoal || ''
      : client?.weightGoal || '';
    const allowedHydratedWeightGoals: string[] = isClientLactating
      ? lactationWeightGoalOptions.map((option) => option.value)
      : generalWeightGoalOptions.map((option) => option.value);

    setAnthropometricEditData({
      weightKg: client?.weightKg != null ? String(client.weightKg) : '',
      heightCm: client?.heightCm != null ? String(client.heightCm) : '',
      waistCircumferenceCm: client?.waistCircumferenceCm != null ? String(client.waistCircumferenceCm) : '',
      hipCircumferenceCm: client?.hipCircumferenceCm != null ? String(client.hipCircumferenceCm) : '',
      activityLevel: normalizedClientActivityLevel,
      weightGoal: allowedHydratedWeightGoals.includes(clientWeightGoal) ? clientWeightGoal : '',
      healthGoal: typeof client?.healthGoal === 'string' ? client.healthGoal : '',
      pregnancyStatus: isClientPregnant ? 'pregnant' : 'none',
      pregnancyTrimester: clientPregnancyTrimester,
      prePregnancyWeightKg: client?.prePregnancyWeightKg != null ? String(client.prePregnancyWeightKg) : '',
      lactationPeriod: client?.lactationPeriod || 'none',
      isTwinPregnancy: Boolean(client?.isTwinPregnancy),
      applyGrowthFactor: getGrowthFactorEnabled(client) ?? true,
      formulaId: currentCalculationFormulaId,
    });
  }, [client, currentCalculationFormulaId, isFemaleClient, normalizedClientActivityLevel]);

  React.useEffect(() => {
    if (!isAnthropometricEditPopoverOpen) return;
    hydrateAnthropometricEditData();
  }, [hydrateAnthropometricEditData, isAnthropometricEditPopoverOpen]);

  React.useEffect(() => {
    setAvailableCalculationFormulas([]);
    setViableCalculationFormulas([]);
    setAvailableDrvGuidelines([]);
    setAllowedActivityLevels(null);
    setShowFormulaValidationModal(false);
    setNonViableFormulaName('');
    setFormulaValidationFallbackName('a recommended formula');
  }, [clientId]);

  React.useEffect(() => {
    if ((!isAnthropometricEditPopoverOpen && !isNutrientGuidelinesPopoverOpen) || !client) return;

    const fetchFormulaOptions = async () => {
      try {
        setIsLoadingFormulaOptions(true);
        const response = await getViableFormulas({
          age: clientAgeForFormulaLookup,
          gender: biologicalSexForCalculations || '',
          countryCode: client.location ? getCountryCode(client.location) : undefined,
        });

        const formulas = response.allFormulas?.length ? response.allFormulas : response.viableFormulas || [];
        setViableCalculationFormulas(response.viableFormulas || []);
        setAvailableCalculationFormulas(formulas);
        setAvailableDrvGuidelines(response.availableDRVGuidelines || []);
        setAllowedActivityLevels(
          resolveAllowedActivityLevels({
            age: clientAge,
            biologicalSex: biologicalSexForCalculations,
            backendAllowedActivityLevels: response.allowedActivityLevels || null,
          })
        );
      } catch (error) {
        console.error('Failed to load formula options:', error);
        try {
          const fallback = await getCalculationFormulas();
          const formulas = Array.isArray(fallback.formulas) ? fallback.formulas : [];
          setAvailableCalculationFormulas(formulas);
          setViableCalculationFormulas(formulas);
          setAvailableDrvGuidelines([]);
          setAllowedActivityLevels(
            resolveAllowedActivityLevels({
              age: clientAge,
              biologicalSex: biologicalSexForCalculations,
              backendAllowedActivityLevels: null,
            })
          );
        } catch (fallbackError) {
          console.error('Failed to load fallback formula options:', fallbackError);
          showErrorToast(fallbackError, 'Failed to load formula options');
        }
      } finally {
        setIsLoadingFormulaOptions(false);
      }
    };

    fetchFormulaOptions();
  }, [
    biologicalSexForCalculations,
    client,
    clientAge,
    clientAgeForFormulaLookup,
    currentCalculationFormulaId,
    currentCalculationFormulaLabel,
    currentDrvFormulaId,
    isAnthropometricEditPopoverOpen,
    isNutrientGuidelinesPopoverOpen,
  ]);

  React.useEffect(() => {
    if (!isNutrientGuidelinesPopoverOpen) return;
    setNutrientGuidelineValue(currentDrvFormulaId);
  }, [currentDrvFormulaId, isNutrientGuidelinesPopoverOpen]);

  React.useEffect(() => {
    if (!isAnthropometricEditPopoverOpen) return;

    setAnthropometricEditData((current) => {
      let next = current;

      if (isInfantForActivity && current.activityLevel) {
        next = { ...next, activityLevel: '' };
      }

      if (!isInfantForActivity) {
        const hasCurrentSelection = Boolean(
          current.activityLevel &&
            activityLevelOptions.some((option) => option.value === current.activityLevel),
        );
        const shouldDefaultActivityLevel = activityLevelOptions.length > 0 && (!current.activityLevel || !hasCurrentSelection);

        if (shouldDefaultActivityLevel) {
          const defaultOption =
            resolvedAllowedActivityLevels?.options.find((option) => option.isDefault) ||
            activityLevelOptions[0];
          if (defaultOption?.value) {
            next = { ...next, activityLevel: defaultOption.value };
          }
        }
      }

      if (!shouldShowGrowthFactorEditor && current.applyGrowthFactor !== true) {
        next = { ...next, applyGrowthFactor: true };
      }

      return next;
    });
  }, [
    isAnthropometricEditPopoverOpen,
    activityLevelOptions,
    isInfantForActivity,
    resolvedAllowedActivityLevels,
    shouldShowGrowthFactorEditor,
  ]);

  React.useEffect(() => {
    if (!isClientGoalsOpen) return;
    setEditableClientGoalRows(clientGoalRows.map((row) => ({ ...row })));
    setHasEditedEnergyMacros(false);
  }, [clientGoalRows, isClientGoalsOpen]);

  const updateEditableGoalRow = (nutrient: string, field: 'grams' | 'kcal', value: string) => {
    if (nutrient === 'Protein' || nutrient === 'Carbohydrate' || nutrient === 'Total Fat') {
      setHasEditedEnergyMacros(true);
    }
    setEditableClientGoalRows((current) =>
      current.map((row) => {
        if (row.nutrient !== nutrient) return row;
        const nextRow = { ...row, [field]: value };
        const parsedValue = Number(value);
        const kcalPerGram = nutrient === 'Total Fat'
          ? 9
          : nutrient === 'Protein' || nutrient === 'Carbohydrate'
            ? 4
            : null;

        if (Number.isFinite(parsedValue)) {
          if (kcalPerGram !== null) {
            if (field === 'grams') nextRow.kcal = String(Math.round(parsedValue * kcalPerGram));
            if (field === 'kcal') nextRow.grams = String(Math.round((parsedValue / kcalPerGram) * 10) / 10);
          }
        }
        return nextRow;
      }),
    );
  };

  const saveClientGoalsFromDialog = async () => {
    if (!client) return;
    try {
      setIsSavingClientGoals(true);
      const targetCalories = derivedDailyCalorieGoal ?? Number.NaN;
      const eerCalories = Number(client.eer);

      if (Number.isFinite(targetCalories) && targetCalories > 0 && Number.isFinite(eerCalories) && eerCalories > 0) {
        const percentageDifference = (Math.abs(targetCalories - eerCalories) / eerCalories) * 100;
        if (percentageDifference > 5) {
          const confirmed = window.confirm(
            `Warning: Your Daily Calorie Goals (${targetCalories} kcal) differs by ${Number(percentageDifference).toFixed(1)}% from the calculated EER (${eerCalories} kcal). This significant difference may not align with your nutritional goals. Do you want to proceed?`,
          );
          if (!confirmed) return;
        }
      }

      const rowsByName = new Map(editableClientGoalRows.map((row) => [row.nutrient, row]));
      const numberOrNull = (value: string | undefined) => {
        if (value === undefined || value === '') return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };
      const currentGoals = (client.clientGoals || {}) as Record<string, any>;
      const proteinGrams = numberOrNull(rowsByName.get('Protein')?.grams);
      const proteinKcal = numberOrNull(rowsByName.get('Protein')?.kcal);
      const carbGrams = numberOrNull(rowsByName.get('Carbohydrate')?.grams);
      const carbKcal = numberOrNull(rowsByName.get('Carbohydrate')?.kcal);
      const fibreValue = numberOrNull(rowsByName.get('Fiber')?.grams);
      const totalFatGrams = numberOrNull(rowsByName.get('Total Fat')?.grams);
      const totalFatKcal = numberOrNull(rowsByName.get('Total Fat')?.kcal);
      const saturatedFatGrams = numberOrNull(rowsByName.get('Sat Fat')?.grams);
      const saturatedFatKcal = numberOrNull(rowsByName.get('Sat Fat')?.kcal);
      const effectiveCalories = Number.isFinite(targetCalories) && targetCalories > 0 ? targetCalories : Number(client.eerOverride || client.eer);

      const clientGoalsPatch = {
        protein: {
          grams: { value: proteinGrams, unit: currentGoals.protein?.grams?.unit || 'g' },
          kcal: { value: proteinKcal, unit: currentGoals.protein?.kcal?.unit || 'kcal' },
        },
        carbohydrates: {
          energyPercent: {
            value: Number.isFinite(effectiveCalories) && effectiveCalories > 0 && carbKcal !== null
              ? Math.round((carbKcal / effectiveCalories) * 1000) / 10
              : currentGoals.carbohydrates?.energyPercent?.value ?? null,
            unit: currentGoals.carbohydrates?.energyPercent?.unit || '% E',
          },
          grams: { value: carbGrams, unit: currentGoals.carbohydrates?.grams?.unit || 'g' },
          kcal: { value: carbKcal, unit: currentGoals.carbohydrates?.kcal?.unit || 'kcal' },
        },
        dietaryFibre: {
          value: fibreValue,
          unit: currentGoals.dietaryFibre?.unit || 'g',
        },
        totalFat: {
          grams: { value: totalFatGrams, unit: currentGoals.totalFat?.grams?.unit || 'g' },
          kcal: { value: totalFatKcal, unit: currentGoals.totalFat?.kcal?.unit || 'kcal' },
        },
        saturatedFat: {
          grams: { value: saturatedFatGrams, unit: currentGoals.saturatedFat?.grams?.unit || 'g' },
          kcal: { value: saturatedFatKcal, unit: currentGoals.saturatedFat?.kcal?.unit || 'kcal' },
        },
      };
      const updatePayload: any = { clientGoalsPatch };

      if (Number.isFinite(targetCalories) && targetCalories > 0) {
        updatePayload.eerOverride = targetCalories;
      }

      const applySavedGoalsLocally = () => {
        updateLocalClient?.((currentClient) => {
          const existingGoals = (currentClient.clientGoals || {}) as Record<string, any>;
          const existingClinicalRules = existingGoals.clinicalRules || {};
          const existingResolvedTargets = existingClinicalRules.resolvedTargets || {};
          const nextResolvedTargets =
            Number.isFinite(targetCalories) && targetCalories > 0
              ? {
                  ...existingResolvedTargets,
                  targetCalories,
                  finalTargetCalories: targetCalories,
                  conditionAdjustedTargetCalories: targetCalories,
                }
              : existingResolvedTargets;

          return {
            ...currentClient,
            eerOverride: Number.isFinite(targetCalories) && targetCalories > 0 ? targetCalories : currentClient.eerOverride,
            targetCalories: Number.isFinite(targetCalories) && targetCalories > 0 ? targetCalories : (currentClient as any).targetCalories,
            finalTargetCalories: Number.isFinite(targetCalories) && targetCalories > 0 ? targetCalories : (currentClient as any).finalTargetCalories,
            conditionAdjustedTargetCalories: Number.isFinite(targetCalories) && targetCalories > 0 ? targetCalories : (currentClient as any).conditionAdjustedTargetCalories,
            clientGoals: {
              ...existingGoals,
              targetCalories: Number.isFinite(targetCalories) && targetCalories > 0 ? targetCalories : existingGoals.targetCalories,
              eerGoalCalories: Number.isFinite(targetCalories) && targetCalories > 0 ? targetCalories : existingGoals.eerGoalCalories,
              protein: { ...existingGoals.protein, ...clientGoalsPatch.protein },
              carbohydrates: { ...existingGoals.carbohydrates, ...clientGoalsPatch.carbohydrates },
              dietaryFibre: { ...existingGoals.dietaryFibre, ...clientGoalsPatch.dietaryFibre },
              totalFat: { ...existingGoals.totalFat, ...clientGoalsPatch.totalFat },
              saturatedFat: { ...existingGoals.saturatedFat, ...clientGoalsPatch.saturatedFat },
              clinicalRules: {
                ...existingClinicalRules,
                resolvedTargets: nextResolvedTargets,
              },
            },
          };
        });
      };

      await updateClient(clientId, updatePayload);
      applySavedGoalsLocally();
      await refreshClientData?.();
      applySavedGoalsLocally();
      setInternalClientGoalsAnchorEl(null);
      onCloseClientGoalsEditor?.();
      showSuccessToast('Success', 'Calories and macros updated successfully');
    } catch (error) {
      console.error('Error saving client goals:', error);
      showErrorToast(error, 'Error');
    } finally {
      setIsSavingClientGoals(false);
    }
  };
  const medicalConditionsSummary = React.useMemo(() => {
    if (!conditionNames.length) return EMPTY_CLIENT_DETAIL_VALUE;
    if (conditionNames.length <= 1) return conditionNames.join(', ');
    return `${conditionNames[0]}, +${conditionNames.length - 1} more`;
  }, [conditionNames]);
  const displayedSummaryItems = summaryItems;
  // Match onboarding: Sex at Birth only when gender is set and is not male/female.
  const rawClientGender = String(client?.gender || '').trim().toLowerCase();
  const shouldShowSexAtBirth =
    !!rawClientGender && rawClientGender !== 'male' && rawClientGender !== 'female';
  const aboutInfoFields = [
    { label: 'FULL NAME', value: aboutDetails.fullName },
    { label: 'DATE OF BIRTH', value: aboutDetails.dateOfBirth },
    { label: 'GENDER', value: aboutDetails.gender },
    ...(shouldShowSexAtBirth ? [{ label: 'SEX AT BIRTH', value: aboutDetails.sexAtBirth }] : []),
    { label: 'PHONE', value: aboutDetails.phone },
    { label: 'EMAIL', value: aboutDetails.email },
    { label: 'LOCATION', value: aboutDetails.location },
    { label: 'CITY / COUNTY', value: aboutDetails.cityOrCounty },
  ];
  const selectedAllergyKeys = React.useMemo(
    () => new Set(selectedAllergies.map((allergy) => allergy.toLowerCase())),
    [selectedAllergies]
  );
  const searchableAllergyOptions = React.useMemo(
    () => toUniqueLabelList([...getDefaultAllergySuggestions(), ...selectedAllergies]),
    [selectedAllergies]
  );
  const normalizedAllergySearchValue = allergySearchValue.trim().toLowerCase();
  const filteredAllergyOptions = React.useMemo(() => {
    return searchableAllergyOptions.filter((option) => {
      const normalizedOption = option.toLowerCase();
      const displayName = getValidAllergies().find(a => a.value.toLowerCase() === normalizedOption)?.name || option;
      const matchesSearch = !normalizedAllergySearchValue || displayName.toLowerCase().includes(normalizedAllergySearchValue);
      return matchesSearch && !selectedAllergyKeys.has(normalizedOption);
    });
  }, [searchableAllergyOptions, normalizedAllergySearchValue, selectedAllergyKeys]);
  const canAddCustomAllergy = React.useMemo(() => {
    if (!normalizedAllergySearchValue) return false;
    if (selectedAllergyKeys.has(normalizedAllergySearchValue)) return false;
    return !searchableAllergyOptions.some((option) => {
      const displayName = getValidAllergies().find(a => a.value.toLowerCase() === option.toLowerCase())?.name || option;
      return displayName.toLowerCase() === normalizedAllergySearchValue;
    });
  }, [normalizedAllergySearchValue, searchableAllergyOptions, selectedAllergyKeys]);
  const selectedIntoleranceKeys = React.useMemo(
    () => new Set(selectedIntolerances.map((intolerance) => intolerance.toLowerCase())),
    [selectedIntolerances]
  );
  const searchableIntoleranceOptions = React.useMemo(
    () => toUniqueLabelList([...getDefaultIntoleranceSuggestions(), ...selectedIntolerances]),
    [selectedIntolerances]
  );
  const normalizedIntoleranceSearchValue = intoleranceSearchValue.trim().toLowerCase();
  const filteredIntoleranceOptions = React.useMemo(() => {
    return searchableIntoleranceOptions.filter((option) => {
      const normalizedOption = option.toLowerCase();
      const displayName = getValidFoodIntolerances().find(i => i.value.toLowerCase() === normalizedOption)?.name || option;
      const matchesSearch = !normalizedIntoleranceSearchValue || displayName.toLowerCase().includes(normalizedIntoleranceSearchValue);
      return matchesSearch && !selectedIntoleranceKeys.has(normalizedOption);
    });
  }, [searchableIntoleranceOptions, normalizedIntoleranceSearchValue, selectedIntoleranceKeys]);
  const canAddCustomIntolerance = React.useMemo(() => {
    if (!normalizedIntoleranceSearchValue) return false;
    if (selectedIntoleranceKeys.has(normalizedIntoleranceSearchValue)) return false;
    return !searchableIntoleranceOptions.some((option) => {
      const displayName = getValidFoodIntolerances().find(i => i.value.toLowerCase() === option.toLowerCase())?.name || option;
      return displayName.toLowerCase() === normalizedIntoleranceSearchValue;
    });
  }, [normalizedIntoleranceSearchValue, searchableIntoleranceOptions, selectedIntoleranceKeys]);
  const selectedMedicalConditionKeys = React.useMemo(
    () =>
      new Set(
        selectedMedicalConditions.map((condition) => mapMedicalConditionToApiValue(condition).toLowerCase()),
      ),
    [selectedMedicalConditions],
  );
  const searchableMedicalConditionOptions = React.useMemo(
    () => toUniqueLabelList([...defaultMedicalConditionSuggestions, ...selectedMedicalConditions]),
    [selectedMedicalConditions],
  );
  const normalizedMedicalConditionSearchValue = medicalConditionSearchValue.trim().toLowerCase();
  const filteredMedicalConditionOptions = React.useMemo(() => {
    return searchableMedicalConditionOptions.filter((option) => {
      const normalizedOption = mapMedicalConditionToApiValue(option).toLowerCase();
      const displayName = resolveMedicalConditionLabel(option);
      const matchesSearch =
        !normalizedMedicalConditionSearchValue ||
        displayName.toLowerCase().includes(normalizedMedicalConditionSearchValue) ||
        normalizedOption.includes(normalizedMedicalConditionSearchValue);
      return matchesSearch && !selectedMedicalConditionKeys.has(normalizedOption);
    });
  }, [
    normalizedMedicalConditionSearchValue,
    searchableMedicalConditionOptions,
    selectedMedicalConditionKeys,
  ]);
  const canAddCustomMedicalCondition = React.useMemo(() => {
    if (!normalizedMedicalConditionSearchValue) return false;
    if (selectedMedicalConditionKeys.has(mapMedicalConditionToApiValue(medicalConditionSearchValue).toLowerCase())) {
      return false;
    }
    return !searchableMedicalConditionOptions.some((option) => {
      const displayName = resolveMedicalConditionLabel(option);
      const normalizedOption = mapMedicalConditionToApiValue(option).toLowerCase();
      return (
        displayName.toLowerCase() === normalizedMedicalConditionSearchValue ||
        normalizedOption === normalizedMedicalConditionSearchValue
      );
    });
  }, [
    medicalConditionSearchValue,
    normalizedMedicalConditionSearchValue,
    searchableMedicalConditionOptions,
    selectedMedicalConditionKeys,
  ]);
  const selectedCuisineKeys = React.useMemo(
    () => new Set(selectedCuisineTypes.map((cuisine) => cuisine.toLowerCase())),
    [selectedCuisineTypes]
  );
  const searchableCuisineOptions = React.useMemo(
    () => toUniqueLabelList([...getDefaultCuisineSuggestions(), ...selectedCuisineTypes]),
    [selectedCuisineTypes]
  );
  const normalizedCuisineSearchValue = cuisineSearchValue.trim().toLowerCase();
  const filteredCuisineOptions = React.useMemo(() => {
    return searchableCuisineOptions.filter((option) => {
      const normalizedOption = option.toLowerCase();
      const displayName = getValidCuisineTypes().find(c => c.value.toLowerCase() === normalizedOption)?.name || option;
      const matchesSearch = !normalizedCuisineSearchValue || displayName.toLowerCase().includes(normalizedCuisineSearchValue);
      return matchesSearch && !selectedCuisineKeys.has(normalizedOption);
    });
  }, [normalizedCuisineSearchValue, searchableCuisineOptions, selectedCuisineKeys]);
  const canAddCustomCuisine = React.useMemo(() => {
    if (!normalizedCuisineSearchValue) return false;
    if (selectedCuisineKeys.has(normalizedCuisineSearchValue)) return false;
    return !searchableCuisineOptions.some((option) => {
      const displayName = getValidCuisineTypes().find(c => c.value.toLowerCase() === option.toLowerCase())?.name || option;
      return displayName.toLowerCase() === normalizedCuisineSearchValue;
    });
  }, [normalizedCuisineSearchValue, searchableCuisineOptions, selectedCuisineKeys]);
  const selectedPreferenceKeys = React.useMemo(
    () => new Set(selectedDietaryPreferences.map((preference) => preference.toLowerCase())),
    [selectedDietaryPreferences]
  );
  const searchablePreferenceOptions = React.useMemo(
    () => toUniqueLabelList([...getDefaultPreferenceSuggestions(), ...selectedDietaryPreferences]),
    [selectedDietaryPreferences]
  );
  const normalizedPreferenceSearchValue = preferenceSearchValue.trim().toLowerCase();
  const filteredPreferenceOptions = React.useMemo(() => {
    return searchablePreferenceOptions.filter((option) => {
      const normalizedOption = option.toLowerCase();
      const displayName = getValidPreferences().find(p => p.value.toLowerCase() === normalizedOption)?.name || option;
      const matchesSearch = !normalizedPreferenceSearchValue || displayName.toLowerCase().includes(normalizedPreferenceSearchValue);
      return matchesSearch && !selectedPreferenceKeys.has(normalizedOption);
    });
  }, [normalizedPreferenceSearchValue, searchablePreferenceOptions, selectedPreferenceKeys]);
  const canAddCustomPreference = React.useMemo(() => {
    if (!normalizedPreferenceSearchValue) return false;
    if (selectedPreferenceKeys.has(normalizedPreferenceSearchValue)) return false;
    return !searchablePreferenceOptions.some((option) => {
      const displayName = getValidPreferences().find(p => p.value.toLowerCase() === option.toLowerCase())?.name || option;
      return displayName.toLowerCase() === normalizedPreferenceSearchValue;
    });
  }, [normalizedPreferenceSearchValue, searchablePreferenceOptions, selectedPreferenceKeys]);
  const cuisineTypesDisplaySummary = React.useMemo(
    () =>
      formatShortDisplayList(selectedCuisineTypes, 'No cuisine types', (value) =>
        getValidCuisineTypes().find((cuisine) => cuisine.value.toLowerCase() === value.toLowerCase())?.name ||
        toTitleDisplayLabel(value),
      ),
    [selectedCuisineTypes],
  );
  const dietaryPreferencesDisplaySummary = React.useMemo(
    () =>
      formatShortDisplayList(selectedDietaryPreferences, dietaryPreferencesSummary, (value) =>
        getValidPreferences().find((preference) => preference.value.toLowerCase() === value.toLowerCase())?.name ||
        toTitleDisplayLabel(value),
      ),
    [dietaryPreferencesSummary, selectedDietaryPreferences],
  );
  const currentStatusRows = [
    { label: 'Meal Compliance', subtitle: 'Last 7 days', value: '78%', valueColor: '#20AEDC' },
    { label: 'Active Plan', subtitle: 'Metabolic Reset', value: 'Week 3/8', valueColor: '#20AEDC' },
    activeSubscriptionSummary
      ? {
          label: 'Active Subscription',
          subtitle: activeSubscriptionSummary.servicesLabel,
          value: activeSubscriptionSummary.periodLabel,
          valueColor: '#20AEDC',
        }
      : {
          label: 'Subscription',
          subtitle: 'No active subscription',
          value: '—',
          valueColor: '#98A2B3',
        },
    {
      label: 'Next Appointment',
      subtitle: nextAppointmentDate ? `${formatDate(nextAppointmentDate, 'hh:mm a', '--:--')} · Video` : 'Not scheduled',
      value: nextAppointmentDate ? formatDate(nextAppointmentDate, 'MMM d', '--') : '--',
      valueColor: '#20AEDC',
    },
  ];
  const isViewResponsesDisabled = questionnairePanelCompletedCount === 0;
  const displayedMetricItems = React.useMemo(
    () => [
      ...metrics.filter((metric) => metric.label !== 'Questionnaire' && metric.label !== 'BMI'),
      { label: 'Questionnaire', value: questionnairePanelCompletedLabel },
    ],
    [metrics, questionnairePanelCompletedLabel],
  );

  const addAllergy = (value: string) => {
    // If the value exists in getValidAllergies(), use its .value
    const foundAllergy = getValidAllergies().find(
      a => a.name.toLowerCase() === value.trim().toLowerCase() || a.value.toLowerCase() === value.trim().toLowerCase()
    );
    const normalizedValue = foundAllergy ? foundAllergy.value : value.trim();
    if (!normalizedValue) return;

    setSelectedAllergies((current) => toUniqueLabelList([...current, normalizedValue]));
    setAllergySearchValue('');
  };

  const removeAllergy = (value: string) => {
    const keyToRemove = value.toLowerCase();
    setSelectedAllergies((current) => current.filter((item) => item.toLowerCase() !== keyToRemove));
  };

  const addIntolerance = (value: string) => {
    // If the value exists in getValidFoodIntolerances(), use its .value
    const foundIntolerance = getValidFoodIntolerances().find(
      i => i.name.toLowerCase() === value.trim().toLowerCase() || i.value.toLowerCase() === value.trim().toLowerCase()
    );
    const normalizedValue = foundIntolerance ? foundIntolerance.value : value.trim();
    if (!normalizedValue) return;

    setSelectedIntolerances((current) =>
      toUniqueLabelList(
        normalizeFoodIntoleranceSelection([...current, normalizedValue], current),
      ),
    );
    setIntoleranceSearchValue('');
  };

  const removeIntolerance = (value: string) => {
    const keyToRemove = value.toLowerCase();
    setSelectedIntolerances((current) => current.filter((item) => item.toLowerCase() !== keyToRemove));
  };

  const addMedicalCondition = (value: string) => {
    const normalizedValue = mapMedicalConditionToApiValue(value);
    if (!normalizedValue) return;

    setSelectedMedicalConditions((current) => toUniqueLabelList([...current, normalizedValue]));
    setMedicalConditionSearchValue('');
  };

  const removeMedicalCondition = (value: string) => {
    const keyToRemove = mapMedicalConditionToApiValue(value).toLowerCase();
    setSelectedMedicalConditions((current) =>
      current.filter((item) => mapMedicalConditionToApiValue(item).toLowerCase() !== keyToRemove),
    );
    setSelectedMedicalConditionSubtypes((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, keyToRemove)) return current;
      const next = { ...current };
      delete next[keyToRemove];
      return next;
    });
  };

  const updateMedicalConditionSubtype = (condition: string, subtype: string) => {
    const conditionId = mapMedicalConditionToApiValue(condition).toLowerCase();
    if (!medicalConditionSubtypeOptionsByCondition[conditionId]) return;

    setSelectedMedicalConditionSubtypes((current) => ({
      ...current,
      [conditionId]: normalizeMedicalConditionSubtypeValue(conditionId, subtype),
    }));
  };

  const renderMedicalConditionSelectedValue = React.useCallback(
    (
      value: string,
      helpers: {
        displayLabel: string;
        onRemove: () => void;
        removeAriaLabel: string;
      },
    ) => {
      const conditionId = mapMedicalConditionToApiValue(value).toLowerCase();
      const subtypeOptions = getMedicalConditionSubtypeOptions(value);
      const subtypeValue = normalizeMedicalConditionSubtypeValue(
        conditionId,
        selectedMedicalConditionSubtypes[conditionId] ?? '',
      );
      const hasDisplaySubtype = Boolean(subtypeValue) && !isUnknownMedicalConditionSubtype(subtypeValue);
      const subtypeControlWidth = hasDisplaySubtype ? 62 : 54;
      const subtypeTooltipLabel = getMedicalConditionSubtypeLabel(conditionId, subtypeValue);

      return (
        <Box
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            minWidth: 0,
            minHeight: 38,
            px: 0.8,
            py: 0.5,
            borderRadius: '8px',
            border: '1px solid #D0D5DD',
            bgcolor: '#fff',
            display: 'grid',
            gridTemplateColumns: subtypeOptions.length
              ? `minmax(0, 1fr) ${subtypeControlWidth}px 18px`
              : 'minmax(0, 1fr) 18px',
            alignItems: 'center',
            columnGap: 0.5,
          }}
        >
          <Typography
            title={helpers.displayLabel}
            sx={{
              minWidth: 0,
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#344054',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {helpers.displayLabel}
          </Typography>

          {subtypeOptions.length ? (
            <Tooltip title={subtypeTooltipLabel} placement="top" arrow>
              <TextField
                select
                value={subtypeValue}
                onChange={(event) => updateMedicalConditionSubtype(value, event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    const selectedValue = String(selected ?? '');
                    return getMedicalConditionSubtypeShortcut(conditionId, selectedValue);
                  },
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        borderRadius: '10px',
                        mt: 0.5,
                        minWidth: 132,
                        overflow: 'hidden',
                        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.18)',
                      },
                    },
                    MenuListProps: {
                      sx: {
                        py: 0.4,
                      },
                    },
                  },
                }}
                sx={{
                  width: subtypeControlWidth,
                  minWidth: 0,
                  flexShrink: 0,
                  '& .MuiOutlinedInput-root': {
                    height: 28,
                    borderRadius: '8px',
                    bgcolor: hasDisplaySubtype ? '#20AEDC' : '#fff',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    color: hasDisplaySubtype ? '#fff' : '#344054',
                    '& fieldset': {
                      borderRadius: '8px',
                      borderColor: hasDisplaySubtype ? '#20AEDC' : '#D0D5DD',
                    },
                    '&:hover fieldset': {
                      borderColor: hasDisplaySubtype ? '#1297c4' : '#98A2B3',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: hasDisplaySubtype ? '#1297c4' : '#667085',
                      borderWidth: 1,
                    },
                  },
                  '& .MuiSelect-select': {
                    minHeight: '0 !important',
                    py: '0 !important',
                    pl: '8px !important',
                    pr: '20px !important',
                    lineHeight: '26px',
                  },
                  '& .MuiSelect-icon': {
                    right: 3,
                    fontSize: 16,
                    color: hasDisplaySubtype ? '#fff' : '#667085',
                  },
                }}
              >
                {subtypeOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    title={option.label}
                    sx={{
                      minHeight: 34,
                      px: 1.6,
                      py: 0.7,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.9rem',
                      color: '#1F2937',
                      '&.Mui-selected': {
                        bgcolor: '#F9FAFB',
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: '#F3F4F6',
                      },
                    }}
                  >
                    {option.shortcut}
                  </MenuItem>
                ))}
              </TextField>
            </Tooltip>
          ) : null}

          <IconButton
            type="button"
            aria-label={helpers.removeAriaLabel}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
              helpers.onRemove();
            }}
            sx={{
              width: 18,
              height: 18,
              p: 0,
              color: '#667085',
              '&:hover': {
                bgcolor: 'rgba(15, 23, 42, 0.06)',
                color: '#344054',
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      );
    },
    [selectedMedicalConditionSubtypes],
  );

  const addCuisineType = (value: string) => {
    const foundCuisine = getValidCuisineTypes().find(
      c => c.name.toLowerCase() === value.trim().toLowerCase() || c.value.toLowerCase() === value.trim().toLowerCase()
    );
    const normalizedValue = foundCuisine ? foundCuisine.value : value.trim();
    if (!normalizedValue) return;

    setSelectedCuisineTypes((current) => toUniqueLabelList([...current, normalizedValue]));
    setCuisineSearchValue('');
  };

  const removeCuisineType = (value: string) => {
    const keyToRemove = value.toLowerCase();
    setSelectedCuisineTypes((current) => current.filter((item) => item.toLowerCase() !== keyToRemove));
  };

  const addDietaryPreference = (value: string) => {
    const foundPreference = getValidPreferences().find(
      p => p.name.toLowerCase() === value.trim().toLowerCase() || p.value.toLowerCase() === value.trim().toLowerCase()
    );
    const normalizedValue = foundPreference ? foundPreference.value : value.trim();
    if (!normalizedValue) return;

    setSelectedDietaryPreferences((current) => toUniqueLabelList([...current, normalizedValue]));
    setPreferenceSearchValue('');
  };

  const removeDietaryPreference = (value: string) => {
    const keyToRemove = value.toLowerCase();
    setSelectedDietaryPreferences((current) => current.filter((item) => item.toLowerCase() !== keyToRemove));
  };

  const handleCurrentStatusButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCurrentStatusAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setAboutAnchorEl(null);
    setAllergiesAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setDietaryPreferencesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handleAboutCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAboutAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setCurrentStatusAnchorEl(null);
    setAllergiesAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setDietaryPreferencesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handleAboutCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setAboutAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setCurrentStatusAnchorEl(null);
    setAllergiesAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handleAllergiesCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAllergiesAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setAllergySearchValue('');
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setDietaryPreferencesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handleAllergiesCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setAllergiesAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setAllergySearchValue('');
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setDietaryPreferencesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handleIntolerancesCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setIntolerancesAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setIntoleranceSearchValue('');
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setAllergiesAnchorEl(null);
    setDietaryPreferencesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handleIntolerancesCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setIntolerancesAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setIntoleranceSearchValue('');
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setAllergiesAnchorEl(null);
    setDietaryPreferencesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handleCuisineTypesCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setDietaryPreferencesPopoverMode('cuisine');
    setDietaryPreferencesAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setCuisineSearchValue('');
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setAllergiesAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handleCuisineTypesCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setDietaryPreferencesPopoverMode('cuisine');
    setDietaryPreferencesAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setCuisineSearchValue('');
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setAllergiesAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handlePreferencesCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setDietaryPreferencesPopoverMode('preferences');
    setDietaryPreferencesAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setPreferenceSearchValue('');
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setAllergiesAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handlePreferencesCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setDietaryPreferencesPopoverMode('preferences');
    setDietaryPreferencesAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setPreferenceSearchValue('');
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setAllergiesAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handleClientGoalsCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onCloseClientGoalsEditor?.();
    setInternalClientGoalsAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setAllergiesAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setDietaryPreferencesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handleClientGoalsCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onCloseClientGoalsEditor?.();
    setInternalClientGoalsAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setAllergiesAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setDietaryPreferencesAnchorEl(null);
    setMedicalConditionsAnchorEl(null);
  };

  const handleMedicalConditionsCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setMedicalConditionsAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setMedicalConditionSearchValue('');
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setAllergiesAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setDietaryPreferencesAnchorEl(null);
  };

  const handleMedicalConditionsCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setMedicalConditionsAnchorEl((current) => (current === event.currentTarget ? null : event.currentTarget));
    setMedicalConditionSearchValue('');
    setCurrentStatusAnchorEl(null);
    setAboutAnchorEl(null);
    setAllergiesAnchorEl(null);
    setIntolerancesAnchorEl(null);
    setDietaryPreferencesAnchorEl(null);
  };

  const closeAboutPopover = () => {
    setIsEditingAbout(false);
    setAboutAnchorEl(null);
  };

  const updateAboutEditField = (field: keyof typeof aboutEditData, value: string) => {
    setAboutEditData((current) => {
      if (field !== 'gender') {
        return { ...current, [field]: value };
      }

      // Match onboarding: binary gender copies into sex at birth; non-binary clears it.
      if (value === 'male' || value === 'female') {
        return { ...current, gender: value, sexAtBirth: value };
      }

      return { ...current, gender: value, sexAtBirth: '' };
    });
  };

  const showSexAtBirthEditField =
    !!aboutEditData.gender &&
    aboutEditData.gender !== 'male' &&
    aboutEditData.gender !== 'female';

  const updateAboutLocation = (value: string) => {
    const countryCode = getPhoneCountryFromLocation(value);
    if (!countryCode) {
      updateAboutEditField('location', value);
      return;
    }

    const nextDialCode = `+${getDialCodeForCountryCode(countryCode)}`;
    setAboutPhoneCountry(countryCode);
    setAboutEditData((current) => ({
      ...current,
      location: value,
      phoneCountryCode: nextDialCode,
    }));
  };

  const updateAboutPhone = (value: string, data: { dialCode?: string; countryCode?: string }) => {
    const dialCode = data.dialCode || '';
    let phonePart = value;

    if (dialCode && value.startsWith(dialCode)) {
      phonePart = value.slice(dialCode.length);
    } else if (dialCode && value.startsWith(`+${dialCode}`)) {
      phonePart = value.slice(dialCode.length + 1);
    }

    const nextDialCode = dialCode ? `+${dialCode}` : '';
    const nextPhoneCountry =
      typeof data.countryCode === 'string' && data.countryCode.trim()
        ? data.countryCode.toLowerCase()
        : aboutPhoneCountry;

    setAboutPhoneCountry(nextPhoneCountry);
    setAboutEditData((current) => ({
      ...current,
      phone: phonePart || (nextDialCode !== current.phoneCountryCode ? current.phone : ''),
      phoneCountryCode: nextDialCode,
    }));
  };

  const saveAboutDetails = async () => {
    if (!client) return;

    const genderForSave = String(aboutEditData.gender || '').trim().toLowerCase();
    const sexAtBirthSource =
      genderForSave === 'male' || genderForSave === 'female'
        ? genderForSave
        : aboutEditData.sexAtBirth;
    const sexAtBirthForSave = normalizeSexAtBirthForSave(sexAtBirthSource);

    if (
      genderForSave &&
      genderForSave !== 'male' &&
      genderForSave !== 'female' &&
      !sexAtBirthForSave
    ) {
      showErrorToast('Sex at birth is required when gender is not Male or Female', 'Error');
      return;
    }

    const payload = {
      fullName: client.fullName?.trim() || null,
      firstName: client.firstName?.trim() || null,
      lastName: client.lastName?.trim() || null,
      dateOfBirth: aboutEditData.dateOfBirth || null,
      gender: aboutEditData.gender || null,
      phoneCountryCode: aboutEditData.phoneCountryCode.trim() || null,
      phone: aboutEditData.phone.trim() || null,
      email: aboutEditData.email.trim() || null,
      location: aboutEditData.location.trim() || null,
      cityOrCounty: aboutEditData.cityOrCounty.trim() || null,
      ...(sexAtBirthForSave ? { sexAtBirth: sexAtBirthForSave } : {}),
    };

    try {
      setIsSavingAbout(true);
      await updateClient(clientId, payload);
      await refreshClientData?.();
      setIsEditingAbout(false);
      showSuccessToast('Success', 'About details updated successfully');
    } catch (error) {
      console.error('Failed to update about details:', error);
      showErrorToast(error, 'Failed to update about details');
    } finally {
      setIsSavingAbout(false);
    }
  };

  const closeAllergiesPopover = () => {
    // Only close if user explicitly initiates close
    setAllergiesAnchorEl(null);
    setAllergySearchValue('');
  };

  const closeIntolerancesPopover = () => {
    // Only close if user explicitly initiates close
    setIntolerancesAnchorEl(null);
    setIntoleranceSearchValue('');
  };

  const closeDietaryPreferencesPopover = () => {
    void saveDietaryPreferencesIfChanged();
    setDietaryPreferencesAnchorEl(null);
    setCuisineSearchValue('');
    setPreferenceSearchValue('');
  };

  const closeClientGoalsPopover = () => {
    setInternalClientGoalsAnchorEl(null);
    onCloseClientGoalsEditor?.();
  };

  const closeCurrentStatusPopover = () => {
    setCurrentStatusAnchorEl(null);
  };

  const closeMedicalConditionsPopover = () => {
    setMedicalConditionsAnchorEl(null);
    setMedicalConditionSearchValue('');
  };

  const closeAnthropometricEditPopover = () => {
    setAnthropometricEditAnchorEl(null);
  };

  const closeNutrientGuidelinesPopover = () => {
    setNutrientGuidelinesAnchorEl(null);
  };

  const saveNutrientGuideline = async () => {
    if (!client) return;

    try {
      setIsSavingNutrientGuideline(true);
      await updateClient(clientId, { drvFormulaId: nutrientGuidelineValue || null });
      await refreshClientData?.();
      closeNutrientGuidelinesPopover();
      showSuccessToast('Success', 'Nutrient guidelines updated successfully');
    } catch (error) {
      console.error('Failed to update nutrient guidelines:', error);
      showErrorToast(error, 'Failed to update nutrient guidelines');
    } finally {
      setIsSavingNutrientGuideline(false);
    }
  };

  const setFormulaValidationModalOpen = React.useCallback((open: boolean) => {
    setShowFormulaValidationModal(open);
    if (!open) {
      setNonViableFormulaName('');
      setFormulaValidationFallbackName('a recommended formula');
    }
  }, []);

  const handleFormulaValidationConfirm = React.useCallback(() => {
    setFormulaValidationModalOpen(false);
  }, [setFormulaValidationModalOpen]);

  const updateAnthropometricEditField = (
    field: keyof typeof anthropometricEditData,
    value: string | boolean,
  ) => {
    let nextValue = value;

    if (field === 'formulaId' && typeof value === 'string' && value) {
      const hasViableFormulas = viableCalculationFormulas.length > 0;
      const isViable = !hasViableFormulas || viableCalculationFormulas.some((formula) => String(formula.id) === value);

      if (!isViable) {
        const selectedFormula = availableCalculationFormulas.find((formula) => String(formula.id) === value);
        const formulaName = selectedFormula?.displayName || selectedFormula?.formulaName || value;
        const fallbackFormula = getPreferredFormulaFallback(viableCalculationFormulas, availableCalculationFormulas);

        if (fallbackFormula) {
          nextValue = String(fallbackFormula.id);
          setNonViableFormulaName(formulaName);
          setFormulaValidationFallbackName(getFormulaFallbackDisplayName(fallbackFormula));
          setShowFormulaValidationModal(true);
        }
      }
    }

    setAnthropometricEditData((current) => {
      const next = { ...current, [field]: nextValue };

      if (field === 'pregnancyStatus' && typeof nextValue === 'string') {
        const currentIsLactating = Boolean(current.lactationPeriod) && current.lactationPeriod !== 'none';
        if (nextValue === 'none' || nextValue === 'not_pregnant') {
          next.pregnancyTrimester = '';
          next.prePregnancyWeightKg = '';
          if (!currentIsLactating) {
            next.isTwinPregnancy = false;
            next.weightGoal = '';
          }
        } else if (nextValue === 'pregnant') {
          if (next.pregnancyTrimester === 'none') {
            next.pregnancyTrimester = '';
          }
          if (!currentIsLactating) {
            next.weightGoal = '';
          }
        }
      }

      if (field === 'lactationPeriod' && typeof nextValue === 'string') {
        const nextIsLactating = Boolean(nextValue) && nextValue !== 'none';
        const wasLactating = Boolean(current.lactationPeriod) && current.lactationPeriod !== 'none';
        const currentIsPregnant = current.pregnancyStatus === 'pregnant';

        if (!nextIsLactating) {
          if (!currentIsPregnant) {
            next.isTwinPregnancy = false;
          }
          next.weightGoal = '';
        } else if (!wasLactating) {
          next.weightGoal = '';
        }
      }

      return next;
    });
  };

  const saveAnthropometricData = async () => {
    if (!client) return;

    const numberOrNull = (value: string) => {
      if (value.trim() === '') return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    if (!anthropometricEditData.formulaId) {
      showErrorToast('Calculation formula is required.', 'Cannot save client');
      return;
    }

    if (shouldShowPregnancyRelatedFields) {
      if (
        isAnthropometricEditPregnant &&
        (!anthropometricEditData.pregnancyTrimester || anthropometricEditData.pregnancyTrimester === 'none')
      ) {
        showErrorToast('Pregnancy trimester is required.', 'Cannot save client');
        return;
      }

      if (isAnthropometricEditPregnant) {
        const prePregnancyWeightKg = numberOrNull(anthropometricEditData.prePregnancyWeightKg);
        if (prePregnancyWeightKg == null || prePregnancyWeightKg <= 0) {
          showErrorToast('Pre-pregnancy weight is required.', 'Cannot save client');
          return;
        }
      }

      if (!anthropometricEditData.lactationPeriod) {
        showErrorToast('Lactation status is required.', 'Cannot save client');
        return;
      }

      if (showLactationWeightGoalEditor && !anthropometricEditData.weightGoal) {
        showErrorToast('Weight loss goal is required for lactating clients.', 'Cannot save client');
        return;
      }
    }

    const updatePayload: Record<string, unknown> = {
      weightKg: numberOrNull(anthropometricEditData.weightKg),
      heightCm: numberOrNull(anthropometricEditData.heightCm),
      waistCircumferenceCm: numberOrNull(anthropometricEditData.waistCircumferenceCm),
      hipCircumferenceCm: numberOrNull(anthropometricEditData.hipCircumferenceCm),
      prePregnancyWeightKg: isAnthropometricEditPregnant
        ? numberOrNull(anthropometricEditData.prePregnancyWeightKg)
        : null,
      weightGoal: showPrimaryWeightGoalEditor ? anthropometricEditData.weightGoal || null : null,
      lactationGoal: showLactationWeightGoalEditor ? anthropometricEditData.weightGoal || null : null,
      bmrFormulaId: anthropometricEditData.formulaId || null,
      healthGoal: anthropometricEditData.healthGoal.trim() || null,
    };

    if (!isInfantForActivity) {
      const mappedActivityLevel = anthropometricEditData.activityLevel
        ? mapActivityLevelForSave(anthropometricEditData.activityLevel)
        : '';
      updatePayload.activityLevel = mappedActivityLevel || null;
    }

    if (shouldShowGrowthFactorEditor) {
      updatePayload.applyGrowthFactor = anthropometricEditData.applyGrowthFactor;
    }

    if (shouldShowPregnancyRelatedFields) {
      updatePayload.pregnancyTrimester = isAnthropometricEditPregnant
        ? anthropometricEditData.pregnancyTrimester
        : 'none';
      updatePayload.lactationPeriod = anthropometricEditData.lactationPeriod || 'none';
      updatePayload.isTwinPregnancy = showPregnancyTypeEditor ? anthropometricEditData.isTwinPregnancy : false;
    }

    try {
      setIsSavingAnthropometricData(true);
      await updateClient(clientId, updatePayload);
      await refreshClientData?.();
      closeAnthropometricEditPopover();
      showSuccessToast('Success', 'Anthropometric data updated successfully');
    } catch (error) {
      console.error('Failed to update anthropometric data:', error);
      showErrorToast(error, 'Failed to update anthropometric data');
    } finally {
      setIsSavingAnthropometricData(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: '1px solid rgba(32, 174, 220, 0.16)',
        overflow: 'hidden',
        bgcolor: '#fff',
        boxShadow: '0 18px 44px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          px: 3,
          pt: 2.15,
          pb: 1.55,
          background: 'linear-gradient(135deg, #25b5dd 0%, #139aca 48%, #087ea6 100%)',
          boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.18)',
          color: '#fff',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'stretch',
            pointerEvents: 'none',
            zIndex: 0,
            opacity: 1,
          }}
        >
          <Box
            component="img"
            src={isAnthropometricExpanded ? expandedPanelGraphic : layer1Graphic}
            alt=""
            sx={{
              width: isAnthropometricExpanded ? 520 : 297,
              height: isAnthropometricExpanded ? 420 : '100%',
              maxHeight: '100%',
              objectFit: isAnthropometricExpanded ? 'contain' : 'cover',
              objectPosition: 'right center',
              display: 'block',
              transform: isAnthropometricExpanded ? 'translateX(72px)' : 'none',
            }}
          />
        </Box>

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            alignItems: 'center',
            gap: 0.75,
            mt: 0.25,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'nowrap',
            }}
          >
            <ClientNameLink
              clientId={clientId}
              className="block min-w-0 text-white hover:text-white focus-visible:text-white"
            >
              <Typography
                component="span"
                sx={{
                  display: 'block',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  color: '#fff',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {clientDisplayName}
              </Typography>
            </ClientNameLink>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.78rem',
                color: 'rgba(255,255,255,0.92)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {emailDisplay}
            </Typography>
            {isPendingDeletionClient(client) ? (
              <Box
                sx={{
                  px: 1,
                  py: 0.2,
                  borderRadius: '999px',
                  bgcolor: '#fff7ed',
                  color: '#b45309',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                Pending deletion
              </Box>
            ) : null}
          </Box>

          {/* {onDeleteClient ? (
            <Button
              onClick={onDeleteClient}
              startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                minHeight: 34,
                px: 1.25,
                borderRadius: '10px',
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.45)',
                bgcolor: 'rgba(180, 35, 24, 0.22)',
                whiteSpace: 'nowrap',
                '&:hover': {
                  bgcolor: 'rgba(180, 35, 24, 0.34)',
                  borderColor: 'rgba(255,255,255,0.7)',
                },
              }}
            >
              Delete
            </Button>
          ) : null} */}
        </Box>

        <Box
          sx={{
            mt: 1.15,
            display: 'grid',
            gap: 1.2,
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
          }}
        >
          {displayedSummaryItems.map((item) => {
            const itemTitle = item.title.toLowerCase();
            const isAboutCard = itemTitle === 'about';
            const isAllergiesCard = itemTitle === 'allergies';
            const isIntolerancesCard = itemTitle === 'intolerances';
            const isMedicalConditionsCard = itemTitle === 'medical conditions';
            const isNutrientGuidelinesCard = itemTitle === 'nutrient guidelines';
            const isInteractiveCard = isAboutCard || isAllergiesCard || isIntolerancesCard || isMedicalConditionsCard || isNutrientGuidelinesCard;
            const ariaDescribedBy = isAboutCard
              ? aboutPopoverId
              : isAllergiesCard
                ? allergiesPopoverId
                : isIntolerancesCard
                  ? intolerancesPopoverId
                  : isMedicalConditionsCard
                    ? medicalConditionsPopoverId
                    : isNutrientGuidelinesCard
                      ? nutrientGuidelinesPopoverId
                  : undefined;
            const isCardOpen = isAboutCard
              ? isAboutPopoverOpen
              : isAllergiesCard
                ? isAllergiesPopoverOpen
                : isIntolerancesCard
                  ? isIntolerancesPopoverOpen
                  : isMedicalConditionsCard
                    ? isMedicalConditionsPopoverOpen
                    : isNutrientGuidelinesCard
                      ? isNutrientGuidelinesPopoverOpen
                  : false;

            return (
              <Box
                key={item.title}
                aria-describedby={ariaDescribedBy}
                role={isInteractiveCard ? 'button' : undefined}
                tabIndex={isInteractiveCard ? 0 : undefined}
                onClick={
                  isAboutCard
                    ? handleAboutCardClick
                    : isAllergiesCard
                      ? handleAllergiesCardClick
                      : isIntolerancesCard
                        ? handleIntolerancesCardClick
                        : isMedicalConditionsCard
                          ? handleMedicalConditionsCardClick
                          : isNutrientGuidelinesCard
                            ? (event) => {
                                hydrateAnthropometricEditData();
                                setNutrientGuidelineValue(currentDrvFormulaId);
                                setNutrientGuidelinesAnchorEl(event.currentTarget);
                              }
                        : undefined
                }
                onKeyDown={
                  isAboutCard
                    ? handleAboutCardKeyDown
                    : isAllergiesCard
                      ? handleAllergiesCardKeyDown
                      : isIntolerancesCard
                        ? handleIntolerancesCardKeyDown
                        : isMedicalConditionsCard
                          ? handleMedicalConditionsCardKeyDown
                          : isNutrientGuidelinesCard
                            ? (event) => {
                                if (event.key !== 'Enter' && event.key !== ' ') return;
                                event.preventDefault();
                                hydrateAnthropometricEditData();
                                setNutrientGuidelineValue(currentDrvFormulaId);
                                setNutrientGuidelinesAnchorEl(event.currentTarget);
                              }
                        : undefined
                }
                sx={{
                  borderRadius: '8px',
                  border: isCardOpen ? '1px solid rgba(255,255,255,0.72)' : '1px solid rgba(255,255,255,0.32)',
                  p: 0.9,
                  minWidth: 0,
                  bgcolor: isCardOpen ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
                  minHeight: cardHeight,
                  height: cardHeight,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  cursor: isInteractiveCard ? 'pointer' : 'default',
                  outline: 'none',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)',
                  ...bluePanelGlassSx,
                  transition: 'background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease',
                  '&:hover': isInteractiveCard
                    ? {
                        bgcolor: 'rgba(255,255,255,0.16)',
                        borderColor: 'rgba(255,255,255,0.66)',
                        transform: 'translateY(-1px)',
                      }
                    : undefined,
                  '&:focus-visible': isInteractiveCard
                    ? {
                        outline: '2px solid rgba(255,255,255,0.9)',
                        outlineOffset: '2px',
                      }
                    : undefined,
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    alignItems: 'center',
                    gap: 0.6,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: blueSectionHeadingFontSize,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: 'rgba(255,255,255,0.86)',
                      }}
                    >
                      {item.title}
                    </Typography>
                    {isAboutCard && item.valueParts?.length ? (
                      <Box
                        sx={{
                          mt: 0.28,
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden',
                        }}
                      >
                        {item.valueParts.map((part, index) => (
                          <Typography
                            key={`${part}-${index}`}
                            component="span"
                            sx={{
                              minWidth: 0,
                              maxWidth: index === item.valueParts.length - 1 ? '45%' : 'none',
                              pr: index < item.valueParts.length - 1 ? 0.7 : 0,
                              mr: index < item.valueParts.length - 1 ? 0.7 : 0,
                              borderRight: index < item.valueParts.length - 1 ? '1px solid rgba(255,255,255,0.42)' : 'none',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              color: '#fff',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {part}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          mt: 0.28,
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#fff',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.value}
                      </Typography>
                    )}
                  </Box>
                  {isNutrientGuidelinesCard ? (
                    <EditOutlinedIcon
                      sx={{
                        fontSize: 17,
                        color: '#fff',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <KeyboardArrowDownRoundedIcon
                      sx={{
                        fontSize: 18,
                        color: '#fff',
                        flexShrink: 0,
                        transform: isCardOpen ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.18s ease',
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Popover
          id={currentStatusPopoverId}
          open={isCurrentStatusPopoverOpen}
          anchorEl={currentStatusAnchorEl}
          onClose={closeCurrentStatusPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableAutoFocus
          disableRestoreFocus
          PaperProps={{
            sx: {
              mt: 1.05,
              width: { xs: 'calc(100vw - 2.25rem)', sm: 'min(100vw - 4.5rem, 460px)' },
              maxWidth: '460px',
              borderRadius: '10px',
              overflow: 'visible',
              border: '1px solid #d5d9e0',
              boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -8,
                left: 'calc(50% - 8px)',
                width: 16,
                height: 16,
                bgcolor: '#f2f4f7',
                borderTop: '1px solid #d5d9e0',
                borderLeft: '1px solid #d5d9e0',
                transform: 'rotate(45deg)',
              },
            },
          }}
        >
          <Box
            sx={{
              px: { xs: 1.75, sm: 2.5 },
              py: 1.45,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#f2f4f7',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '8px',
                  bgcolor: '#e8f6fc',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <CurrentStatusGlyph size={16} />
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: '#1f2937',
                }}
              >
                Current Status
              </Typography>
            </Box>
            <IconButton
              aria-label="Close current status details"
              onClick={closeCurrentStatusPopover}
              sx={{
                width: 32,
                height: 32,
                bgcolor: '#20AEDC',
                color: '#fff',
                '&:hover': { bgcolor: '#1297c4' },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 0.85, sm: 1 }, bgcolor: '#fff' }}>
            {currentStatusRows.map((row, index) => (
              <Box
                key={row.label}
                sx={{
                  py: 1.35,
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom: index === currentStatusRows.length - 1 ? 'none' : '1px solid #EAECF0',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      color: '#344054',
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.2,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.75rem',
                      color: '#98A2B3',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {row.subtitle}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '1.02rem',
                    fontWeight: 700,
                    color: row.valueColor,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Popover>

        <Popover
          id={medicalConditionsPopoverId}
          open={isMedicalConditionsPopoverOpen}
          anchorEl={medicalConditionsAnchorEl}
          onClose={closeMedicalConditionsPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableAutoFocus
          disableRestoreFocus
          PaperProps={{
            sx: {
              mt: 1.05,
              width: { xs: 'calc(100vw - 3.25rem)', sm: 'min(100vw - 5rem, 440px)' },
              maxWidth: '440px',
              borderRadius: '10px',
              overflow: 'visible',
              border: '1px solid #d5d9e0',
              boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -8,
                right: 24,
                width: 16,
                height: 16,
                bgcolor: '#f2f4f7',
                borderTop: '1px solid #d5d9e0',
                borderLeft: '1px solid #d5d9e0',
                transform: 'rotate(45deg)',
              },
            },
          }}
        >
          <Box
            sx={{
              px: { xs: 1.75, sm: 2.5 },
              py: 1.45,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#f2f4f7',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '8px',
                  bgcolor: '#e8f6fc',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <MedicalConditionsGlyph size={16} />
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: '#1f2937',
                }}
              >
                Medical Conditions
              </Typography>
            </Box>
            <IconButton
              aria-label="Close medical conditions details"
              onClick={closeMedicalConditionsPopover}
              sx={{
                width: 32,
                height: 32,
                bgcolor: '#20AEDC',
                color: '#fff',
                '&:hover': { bgcolor: '#1297c4' },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.9, sm: 2 }, bgcolor: '#fff' }}>
            <SearchableTagSelector
              searchValue={medicalConditionSearchValue}
              onSearchChange={setMedicalConditionSearchValue}
              onSearchEnter={() => {
                if (!medicalConditionSearchValue.trim()) return;
                addMedicalCondition(medicalConditionSearchValue);
              }}
              searchPlaceholder="Search or add other conditions..."
              addSectionTitle="Add Medical Conditions"
              currentSectionTitle="Current Medical Conditions"
              filteredOptions={filteredMedicalConditionOptions}
              canAddCustom={canAddCustomMedicalCondition}
              customOptionLabel={
                MEDICAL_CONDITIONS_API_OPTIONS.find(
                  (condition) =>
                    condition.id.toLowerCase() === medicalConditionSearchValue.trim().toLowerCase() ||
                    condition.name.toLowerCase() === medicalConditionSearchValue.trim().toLowerCase(),
                )?.name || medicalConditionSearchValue.trim()
              }
              onAddOption={addMedicalCondition}
              selectedValues={selectedMedicalConditions}
              onRemoveSelected={removeMedicalCondition}
              resolveLabel={resolveMedicalConditionLabel}
              renderSelectedValue={renderMedicalConditionSelectedValue}
              emptyOptionsText="No additional medical conditions found"
              emptySelectedText="No medical conditions added yet"
              addAriaLabel={(displayLabel) => `Add ${displayLabel} medical condition`}
              removeAriaLabel={(displayLabel) => `Remove ${displayLabel} medical condition`}
            />
          </Box>
        </Popover>

        <Popover
          id={aboutPopoverId}
          open={isAboutPopoverOpen}
          anchorEl={aboutAnchorEl}
          onClose={closeAboutPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableAutoFocus
          disableRestoreFocus
          PaperProps={{
            sx: {
              mt: 1.05,
              width: { xs: 'calc(100vw - 2.25rem)', sm: 'min(100vw - 4.5rem, 500px)' },
              maxWidth: '500px',
              borderRadius: '10px',
              overflow: 'visible',
              border: '1px solid #d5d9e0',
              boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -8,
                left: 34,
                width: 16,
                height: 16,
                bgcolor: '#f2f4f7',
                borderTop: '1px solid #d5d9e0',
                borderLeft: '1px solid #d5d9e0',
                transform: 'rotate(45deg)',
              },
            },
          }}
        >
          <Box
            sx={{
              px: { xs: 1.75, sm: 2.5 },
              py: 1.45,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#f2f4f7',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonRoundedIcon sx={{ color: '#20AEDC', fontSize: 19 }} />
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: '#1f2937',
                }}
              >
                About
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {!isEditingAbout && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    hydrateAboutEditData();
                    setIsEditingAbout(true);
                  }}
                  sx={{
                    minHeight: 28,
                    minWidth: 0,
                    px: 0.5,
                    color: '#20AEDC',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                    '& .MuiButton-startIcon': { mr: 0.4 },
                  }}
                >
                  Edit
                </Button>
              )}
              <IconButton
                aria-label="Close about details"
                onClick={closeAboutPopover}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: '#20AEDC',
                  color: '#fff',
                  '&:hover': { bgcolor: '#1297c4' },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.8, sm: 2 }, bgcolor: '#fff' }}>
            {isEditingAbout ? (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    columnGap: { xs: 0, sm: 1.75 },
                    rowGap: 1.65,
                  }}
                >
                  <TextField
                    label="Full name"
                    size="small"
                    value={aboutEditData.fullName}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={{
                      ...anthropometricEditFieldSx,
                      '& .MuiInputBase-input': { cursor: 'default' },
                      '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' },
                    }}
                  />
                  <TextField label="Date of birth" type="date" size="small" value={aboutEditData.dateOfBirth} onChange={(event) => updateAboutEditField('dateOfBirth', event.target.value)} fullWidth InputLabelProps={{ shrink: true }} sx={anthropometricEditFieldSx} />
                  <TextField select label="Gender" size="small" value={aboutEditData.gender} onChange={(event) => updateAboutEditField('gender', event.target.value)} fullWidth sx={anthropometricEditFieldSx}>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="non_binary">Non-binary</MenuItem>
                    <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                  {showSexAtBirthEditField ? (
                    <TextField
                      select
                      label="Sex at birth"
                      size="small"
                      value={aboutEditData.sexAtBirth}
                      onChange={(event) => updateAboutEditField('sexAtBirth', event.target.value)}
                      fullWidth
                      required
                      sx={anthropometricEditFieldSx}
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                    </TextField>
                  ) : null}
                  <TextField label="Email" size="small" value={aboutEditData.email} onChange={(event) => updateAboutEditField('email', event.target.value)} fullWidth sx={anthropometricEditFieldSx} />
                  <TextField select label="Country" size="small" value={aboutEditData.location} onChange={(event) => updateAboutLocation(event.target.value)} fullWidth sx={anthropometricEditFieldSx}>
                    {countryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField label="City / County" size="small" value={aboutEditData.cityOrCounty} onChange={(event) => updateAboutEditField('cityOrCounty', event.target.value)} fullWidth sx={anthropometricEditFieldSx} />
                  <Box sx={{ minWidth: 0, position: 'relative' }}>
                    <Typography
                      sx={{
                        position: 'absolute',
                        top: -7,
                        left: 12,
                        zIndex: 2,
                        px: '1px',
                        bgcolor: '#fff',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        lineHeight: 1,
                        color: '#667085',
                      }}
                    >
                      Phone
                    </Typography>
                    <PhoneInput
                      key={`about-phone-${aboutPhoneCountry}-${aboutEditData.phoneCountryCode}`}
                      country={aboutPhoneCountry}
                      value={`${aboutEditData.phoneCountryCode.replace('+', '')}${aboutEditData.phone}`}
                      onChange={updateAboutPhone}
                      countryCodeEditable={false}
                      inputProps={{ name: 'phone', autoFocus: false }}
                      containerClass="client-details-about-phone"
                      inputStyle={{
                        width: '100%',
                        height: 40,
                        borderRadius: 10,
                        borderColor: '#d9e4ea',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                      buttonStyle={{
                        height: 40,
                        borderTopLeftRadius: 10,
                        borderBottomLeftRadius: 10,
                        borderColor: '#d9e4ea',
                        background: '#fff',
                      }}
                      dropdownStyle={{ zIndex: 9999 }}
                    />
                  </Box>
                </Box>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      hydrateAboutEditData();
                      setIsEditingAbout(false);
                    }}
                    disabled={isSavingAbout}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 700 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={saveAboutDetails}
                    disabled={isSavingAbout}
                    sx={{ minWidth: 104, borderRadius: '8px', bgcolor: '#20AEDC', textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 700, boxShadow: 'none', '&:hover': { bgcolor: '#1297c4', boxShadow: 'none' } }}
                  >
                    {isSavingAbout ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
                  </Button>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: { xs: 1.6, sm: 2 },
                  columnGap: { sm: 3.5 },
                  rowGap: { sm: 3 },
                }}
              >
                {aboutInfoFields.map((field) => (
                  <Box key={field.label} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: '#98A2B3',
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.4,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.88rem',
                        fontWeight: 500,
                        lineHeight: 1.3,
                        color: '#344054',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {field.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Popover>

        <Popover
          id={allergiesPopoverId}
          open={isAllergiesPopoverOpen}
          anchorEl={allergiesAnchorEl}
          onClose={closeAllergiesPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableAutoFocus
          disableRestoreFocus
          PaperProps={{
            sx: {
              mt: 1.05,
              width: { xs: 'calc(100vw - 3.25rem)', sm: 'min(100vw - 5rem, 440px)' },
              maxWidth: '440px',
              borderRadius: '10px',
              overflow: 'visible',
              border: '1px solid #d5d9e0',
              boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -8,
                left: 34,
                width: 16,
                height: 16,
                bgcolor: '#f2f4f7',
                borderTop: '1px solid #d5d9e0',
                borderLeft: '1px solid #d5d9e0',
                transform: 'rotate(45deg)',
              },
            },
          }}
        >
          <Box
            sx={{
              px: { xs: 1.75, sm: 2.5 },
              py: 1.45,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#f2f4f7',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '8px',
                  bgcolor: '#e8f6fc',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <AllergiesGlyph size={16} />
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: '#1f2937',
                }}
              >
                Allergies
              </Typography>
            </Box>
            <IconButton
              aria-label="Close allergies details"
              onClick={closeAllergiesPopover}
              sx={{
                width: 32,
                height: 32,
                bgcolor: '#20AEDC',
                color: '#fff',
                '&:hover': { bgcolor: '#1297c4' },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.9, sm: 2 }, bgcolor: '#fff' }}>
            <SearchableTagSelector
              searchValue={allergySearchValue}
              onSearchChange={setAllergySearchValue}
              onSearchEnter={() => {
                if (!allergySearchValue.trim()) return;
                addAllergy(allergySearchValue);
              }}
              searchPlaceholder="Search or add allergen..."
              addSectionTitle="Add Allergies"
              currentSectionTitle="Current Allergies"
              filteredOptions={filteredAllergyOptions}
              canAddCustom={canAddCustomAllergy}
              customOptionLabel={
                getValidAllergies().find(
                  (allergy) =>
                    allergy.value.toLowerCase() === allergySearchValue.trim().toLowerCase(),
                )?.name || allergySearchValue.trim()
              }
              onAddOption={addAllergy}
              selectedValues={selectedAllergies}
              onRemoveSelected={removeAllergy}
              resolveLabel={(value) =>
                getValidAllergies().find((allergy) => allergy.value.toLowerCase() === value.toLowerCase())
                  ?.name || value
              }
              emptyOptionsText="No additional allergens found"
              emptySelectedText="No allergies added yet"
              addAriaLabel={(displayLabel) => `Add ${displayLabel} allergy`}
              removeAriaLabel={(displayLabel) => `Remove ${displayLabel} allergy`}
            />
          </Box>
        </Popover>

        <Popover
          id={intolerancesPopoverId}
          open={isIntolerancesPopoverOpen}
          anchorEl={intolerancesAnchorEl}
          onClose={closeIntolerancesPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableAutoFocus
          disableRestoreFocus
          PaperProps={{
            sx: {
              mt: 1.05,
              width: { xs: 'calc(100vw - 3.25rem)', sm: 'min(100vw - 5rem, 440px)' },
              maxWidth: '440px',
              borderRadius: '10px',
              overflow: 'visible',
              border: '1px solid #d5d9e0',
              boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -8,
                left: 34,
                width: 16,
                height: 16,
                bgcolor: '#f2f4f7',
                borderTop: '1px solid #d5d9e0',
                borderLeft: '1px solid #d5d9e0',
                transform: 'rotate(45deg)',
              },
            },
          }}
        >
          <Box
            sx={{
              px: { xs: 1.75, sm: 2.5 },
              py: 1.45,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#f2f4f7',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '8px',
                  bgcolor: '#e8f6fc',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <IntolerancesGlyph size={16} />
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: '#1f2937',
                }}
              >
                Intolerances
              </Typography>
            </Box>
            <IconButton
              aria-label="Close intolerances details"
              onClick={closeIntolerancesPopover}
              sx={{
                width: 32,
                height: 32,
                bgcolor: '#20AEDC',
                color: '#fff',
                '&:hover': { bgcolor: '#1297c4' },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.9, sm: 2 }, bgcolor: '#fff' }}>
            <SearchableTagSelector
              searchValue={intoleranceSearchValue}
              onSearchChange={setIntoleranceSearchValue}
              onSearchEnter={() => {
                if (!intoleranceSearchValue.trim()) return;
                addIntolerance(intoleranceSearchValue);
              }}
              searchPlaceholder="Search or add Intolerances"
              addSectionTitle="Add Intolerances"
              currentSectionTitle="Current Intolerances"
              filteredOptions={filteredIntoleranceOptions}
              canAddCustom={canAddCustomIntolerance}
              customOptionLabel={
                getValidFoodIntolerances().find(
                  (intolerance) =>
                    intolerance.value.toLowerCase() ===
                    intoleranceSearchValue.trim().toLowerCase(),
                )?.name || intoleranceSearchValue.trim()
              }
              onAddOption={addIntolerance}
              selectedValues={selectedIntolerances}
              onRemoveSelected={removeIntolerance}
              resolveLabel={(value) =>
                getValidFoodIntolerances().find(
                  (intolerance) => intolerance.value.toLowerCase() === value.toLowerCase(),
                )?.name || value
              }
              emptyOptionsText="No additional intolerances found"
              emptySelectedText="No intolerances added yet"
              addAriaLabel={(displayLabel) => `Add ${displayLabel} intolerance`}
              removeAriaLabel={(displayLabel) => `Remove ${displayLabel} intolerance`}
            />
          </Box>
        </Popover>

        <Popover
          id={dietaryPreferencesPopoverId}
          open={isDietaryPreferencesPopoverOpen}
          anchorEl={dietaryPreferencesAnchorEl}
          onClose={closeDietaryPreferencesPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableAutoFocus
          disableRestoreFocus
          PaperProps={{
            sx: {
              mt: 1.05,
              width: { xs: 'calc(100vw - 2.25rem)', md: 'min(100vw - 4.5rem, 640px)' },
              maxWidth: '640px',
              borderRadius: '8px',
              overflow: 'visible',
              border: '1px solid #d5d9e0',
              boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -8,
                right: 52,
                width: 16,
                height: 16,
                bgcolor: '#f7f7f8',
                borderTop: '1px solid #d5d9e0',
                borderLeft: '1px solid #d5d9e0',
                transform: 'rotate(45deg)',
              },
            },
          }}
        >
          <Box sx={{ bgcolor: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
            <Box
              sx={{
                px: 2.6,
                py: 1.6,
                bgcolor: '#f7f7f8',
                borderBottom: '1px solid #eef0f3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '6px',
                    bgcolor: '#E9F8FD',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <AllergiesGlyph size={16} />
                </Box>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#1F2937' }}>
                  {dietaryPreferencesPopoverMode === 'cuisine' ? 'Cuisine Types' : 'Preferences'}
                </Typography>
              </Box>
              <IconButton
                aria-label="Close dietary preferences details"
                onClick={closeDietaryPreferencesPopover}
                sx={{
                  width: 31,
                  height: 31,
                  bgcolor: '#20AEDC',
                  color: '#fff',
                  '&:hover': { bgcolor: '#1297c4' },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Box>

            <Box
              sx={{
                px: 2.6,
                py: 2.1,
                maxHeight: { xs: 'calc(100vh - 13rem)', md: 720 },
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#20AEDC', borderRadius: 999 },
              }}
            >
              {dietaryPreferencesPopoverMode === 'cuisine' && (
              <Box
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  p: { xs: 1.4, sm: 1.7 },
                  bgcolor: '#fff',
                }}
              >
                <Typography sx={{ mb: 1.0, fontFamily: 'Inter, sans-serif', fontSize: '1.08rem', fontWeight: 700, color: '#6B7280' }}>
                  Cuisine Types
                </Typography>
                <SearchableTagSelector
                  searchValue={cuisineSearchValue}
                  onSearchChange={setCuisineSearchValue}
                  onSearchEnter={() => {
                    if (!cuisineSearchValue.trim()) return;
                    addCuisineType(cuisineSearchValue);
                  }}
                  searchPlaceholder="Search or add Cuisine Types"
                  addSectionTitle="Add Cuisine Types"
                  currentSectionTitle="Current Cuisine Types"
                  filteredOptions={filteredCuisineOptions}
                  canAddCustom={canAddCustomCuisine}
                  customOptionLabel={
                    getValidCuisineTypes().find(
                      (cuisine) => cuisine.value.toLowerCase() === cuisineSearchValue.trim().toLowerCase(),
                    )?.name || toTitleDisplayLabel(cuisineSearchValue)
                  }
                  onAddOption={addCuisineType}
                  selectedValues={selectedCuisineTypes}
                  onRemoveSelected={removeCuisineType}
                  resolveLabel={(value) =>
                    getValidCuisineTypes().find((cuisine) => cuisine.value.toLowerCase() === value.toLowerCase())?.name || toTitleDisplayLabel(value)
                  }
                  emptyOptionsText="No additional cuisine types found"
                  emptySelectedText="No cuisine types added yet"
                  addAriaLabel={(displayLabel) => `Add ${displayLabel} cuisine type`}
                  removeAriaLabel={(displayLabel) => `Remove ${displayLabel} cuisine type`}
                  listMaxHeight={238}
                  appearance="ingredient-modal"
                />
              </Box>
              )}

              {dietaryPreferencesPopoverMode === 'preferences' && (
              <Box
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  p: { xs: 1.4, sm: 1.7 },
                  bgcolor: '#fff',
                }}
              >
                <Typography sx={{ mb: 1.0, fontFamily: 'Inter, sans-serif', fontSize: '1.08rem', fontWeight: 700, color: '#6B7280' }}>
                  Preferences
                </Typography>
                <SearchableTagSelector
                  searchValue={preferenceSearchValue}
                  onSearchChange={setPreferenceSearchValue}
                  onSearchEnter={() => {
                    if (!preferenceSearchValue.trim()) return;
                    addDietaryPreference(preferenceSearchValue);
                  }}
                  searchPlaceholder="Search or add Preferences"
                  addSectionTitle="Add Preferences"
                  currentSectionTitle="Current Preferences"
                  filteredOptions={filteredPreferenceOptions}
                  canAddCustom={canAddCustomPreference}
                  customOptionLabel={
                    getValidPreferences().find(
                      (preference) => preference.value.toLowerCase() === preferenceSearchValue.trim().toLowerCase(),
                    )?.name || toTitleDisplayLabel(preferenceSearchValue)
                  }
                  onAddOption={addDietaryPreference}
                  selectedValues={selectedDietaryPreferences}
                  onRemoveSelected={removeDietaryPreference}
                  resolveLabel={(value) =>
                    getValidPreferences().find((preference) => preference.value.toLowerCase() === value.toLowerCase())?.name || toTitleDisplayLabel(value)
                  }
                  emptyOptionsText="No additional preferences found"
                  emptySelectedText="No preferences added yet"
                  addAriaLabel={(displayLabel) => `Add ${displayLabel} preference`}
                  removeAriaLabel={(displayLabel) => `Remove ${displayLabel} preference`}
                  listMaxHeight={238}
                  appearance="ingredient-modal"
                />
              </Box>
              )}
            </Box>
          </Box>
        </Popover>

        <Popover
          id={nutrientGuidelinesPopoverId}
          open={isNutrientGuidelinesPopoverOpen}
          anchorEl={nutrientGuidelinesAnchorEl}
          onClose={closeNutrientGuidelinesPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableAutoFocus
          disableRestoreFocus
          PaperProps={{
            sx: {
              mt: 1,
              width: 360,
              maxWidth: 'calc(100vw - 48px)',
              borderRadius: '12px',
              border: '1px solid #d5d9e0',
              boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
              overflow: 'hidden',
            },
          }}
        >
          <Box sx={{ p: 2, bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#101828' }}>
                  Edit Nutrient Guidelines
                </Typography>
                <Typography sx={{ mt: 0.35, fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#667085' }}>
                  Select the DRV formula used for targets.
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={closeNutrientGuidelinesPopover}
                sx={{ width: 30, height: 30, bgcolor: '#f2f4f7', '&:hover': { bgcolor: '#e4e7ec' } }}
              >
                <CloseRoundedIcon sx={{ fontSize: 18, color: '#344054' }} />
              </IconButton>
            </Box>

            <TextField
              select
              label="DRV formula"
              value={nutrientGuidelineValue}
              onChange={(event) => setNutrientGuidelineValue(event.target.value)}
              size="small"
              fullWidth
              disabled={isLoadingFormulaOptions}
              sx={{ mt: 2 }}
            >
              {availableDrvGuidelines.map((guideline) => (
                <MenuItem key={guideline.id} value={String(guideline.id)}>
                  {guideline.displayName || guideline.name || guideline.id}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={closeNutrientGuidelinesPopover}
                disabled={isSavingNutrientGuideline}
                sx={{ borderRadius: '8px', textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 700 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveNutrientGuideline}
                disabled={isSavingNutrientGuideline || isLoadingFormulaOptions}
                sx={{
                  minWidth: 104,
                  borderRadius: '8px',
                  bgcolor: '#20AEDC',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#1297c4', boxShadow: 'none' },
                }}
              >
                {isSavingNutrientGuideline ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
              </Button>
            </Box>
          </Box>
        </Popover>

        <Box
          sx={{
            mt: 1.1,
            display: 'grid',
            gap: 1.1,
            alignItems: 'start',
            gridTemplateColumns: {
              xs: '1fr',
              lg: 'minmax(430px, 1.15fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)',
            },
          }}
        >
          <Box
            sx={{
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.32)',
              p: 0.8,
              bgcolor: 'rgba(255,255,255,0.1)',
              minWidth: 0,
              minHeight: cardHeight,
              height: cardHeight,
              display: 'flex',
              alignItems: 'center',
              gridColumn: { xs: '1 / -1', lg: 'auto' },
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
              ...bluePanelGlassSx,
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'grid',
                gap: isAnthropometricExpanded ? 0.55 : 0.35,
                alignItems: 'center',
                gridTemplateColumns: `repeat(${Math.max(displayedMetricItems.length, 1)}, minmax(76px, 1fr))`,
              }}
            >
              {displayedMetricItems.map((metric) => {
                const isEnergyMetric = metric.label === 'BMR' || metric.label === 'EER';
                const isQuestionnaireMetric = metric.label === 'Questionnaire';

                return (
                <Box
                  key={metric.label}
                  role={isQuestionnaireMetric && !isViewResponsesDisabled ? 'button' : undefined}
                  tabIndex={isQuestionnaireMetric && !isViewResponsesDisabled ? 0 : undefined}
                  onClick={isQuestionnaireMetric && !isViewResponsesDisabled ? onOpenQuestionnaires : undefined}
                  onKeyDown={
                    isQuestionnaireMetric && !isViewResponsesDisabled
                      ? (event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          onOpenQuestionnaires();
                        }
                      : undefined
                  }
                  sx={{
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: isAnthropometricExpanded ? 0.32 : 0.22,
                    cursor: isQuestionnaireMetric && !isViewResponsesDisabled ? 'pointer' : 'default',
                    borderRadius: '6px',
                    outline: 'none',
                    '&:focus-visible': isQuestionnaireMetric && !isViewResponsesDisabled
                      ? {
                          outline: '2px solid rgba(255,255,255,0.9)',
                          outlineOffset: '2px',
                        }
                      : undefined,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: 1.1,
                      textTransform: 'uppercase',
                      letterSpacing: isQuestionnaireMetric ? '0.02em' : '0.06em',
                      color: '#fff',
                      whiteSpace: isQuestionnaireMetric ? 'nowrap' : 'normal',
                      overflowWrap: isQuestionnaireMetric ? 'normal' : 'anywhere',
                      fontSize: isQuestionnaireMetric ? '0.68rem' : blueSectionHeadingFontSize,
                    }}
                  >
                    {metric.label}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.55,
                      minWidth: 0,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      overflow: 'visible',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        lineHeight: 1.2,
                        color: '#fff',
                        whiteSpace: isEnergyMetric ? 'normal' : 'pre-line',
                        overflowWrap: 'anywhere',
                        wordBreak: isEnergyMetric ? 'break-word' : 'normal',
                      }}
                    >
                      {isEnergyMetric ? metric.value.replace('/day', '/d ay') : metric.value}
                    </Typography>
                    {isQuestionnaireMetric && !isViewResponsesDisabled && (
                      <Button
                        variant="text"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenQuestionnaires();
                        }}
                        sx={{
                          minWidth: 0,
                          p: 0,
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          lineHeight: 1.2,
                          color: '#fff',
                          textDecoration: 'underline',
                          textUnderlineOffset: '3px',
                          '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                        }}
                      >
                        View Responses
                      </Button>
                    )}
                  </Box>
                </Box>
                );
              })}
            </Box>
          </Box>

          <Box
                data-demo-id="meal-plan-cuisine-types"
                aria-describedby={dietaryPreferencesPopoverId}
                role="button"
                tabIndex={0}
                onClick={handleCuisineTypesCardClick}
                onKeyDown={handleCuisineTypesCardKeyDown}
                sx={{
                  borderRadius: '8px',
                  border: isDietaryPreferencesPopoverOpen && dietaryPreferencesPopoverMode === 'cuisine' ? '1px solid rgba(255,255,255,0.72)' : '1px solid rgba(255,255,255,0.32)',
                  p: 0.8,
                  bgcolor: isDietaryPreferencesPopoverOpen && dietaryPreferencesPopoverMode === 'cuisine' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
                  minWidth: 0,
                  minHeight: cardHeight,
                  height: cardHeight,
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'center',
                  gap: 0.6,
                  cursor: 'pointer',
                  outline: 'none',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
                  ...bluePanelGlassSx,
                  transition: 'background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.16)',
                    borderColor: 'rgba(255,255,255,0.66)',
                    transform: 'translateY(-1px)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid rgba(255,255,255,0.9)',
                    outlineOffset: '2px',
                  },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: blueSectionHeadingFontSize, lineHeight: 1.1, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.86)' }}>
                    Cuisine Types
                  </Typography>
                  <Typography sx={{ mt: 0.28, fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {cuisineTypesDisplaySummary}
                  </Typography>
                </Box>
                <KeyboardArrowDownRoundedIcon
                  sx={{
                    fontSize: 18,
                    color: '#fff',
                    flexShrink: 0,
                    transform: isDietaryPreferencesPopoverOpen && dietaryPreferencesPopoverMode === 'cuisine' ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.18s ease',
                  }}
                />
          </Box>

          <Box
                data-demo-id="meal-plan-dietary-preferences"
                aria-describedby={dietaryPreferencesPopoverId}
                role="button"
                tabIndex={0}
                onClick={handlePreferencesCardClick}
                onKeyDown={handlePreferencesCardKeyDown}
                sx={{
                  borderRadius: '8px',
                  border: isDietaryPreferencesPopoverOpen && dietaryPreferencesPopoverMode === 'preferences' ? '1px solid rgba(255,255,255,0.72)' : '1px solid rgba(255,255,255,0.32)',
                  p: 0.8,
                  bgcolor: isDietaryPreferencesPopoverOpen && dietaryPreferencesPopoverMode === 'preferences' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
                  minWidth: 0,
                  minHeight: cardHeight,
                  height: cardHeight,
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'center',
                  gap: 0.6,
                  cursor: 'pointer',
                  outline: 'none',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
                  ...bluePanelGlassSx,
                  transition: 'background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.16)',
                    borderColor: 'rgba(255,255,255,0.66)',
                    transform: 'translateY(-1px)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid rgba(255,255,255,0.9)',
                    outlineOffset: '2px',
                  },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: blueSectionHeadingFontSize, lineHeight: 1.1, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.86)' }}>
                    Preferences
                  </Typography>
                  <Typography sx={{ mt: 0.28, fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {dietaryPreferencesDisplaySummary}
                  </Typography>
                </Box>
                <KeyboardArrowDownRoundedIcon
                  sx={{
                    fontSize: 18,
                    color: '#fff',
                    flexShrink: 0,
                    transform: isDietaryPreferencesPopoverOpen && dietaryPreferencesPopoverMode === 'preferences' ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.18s ease',
                  }}
                />
          </Box>

          <Box
                aria-describedby={medicalConditionsPopoverId}
                role="button"
                tabIndex={0}
                onClick={handleMedicalConditionsCardClick}
                onKeyDown={handleMedicalConditionsCardKeyDown}
                sx={{
                  borderRadius: '8px',
                  border: isMedicalConditionsPopoverOpen ? '1px solid rgba(255,255,255,0.72)' : '1px solid rgba(255,255,255,0.32)',
                  p: 0.8,
                  bgcolor: isMedicalConditionsPopoverOpen ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
                  minWidth: 0,
                  minHeight: cardHeight,
                  height: cardHeight,
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'center',
                  gap: 0.6,
                  cursor: 'pointer',
                  outline: 'none',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
                  ...bluePanelGlassSx,
                  transition: 'background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.16)',
                    borderColor: 'rgba(255,255,255,0.66)',
                    transform: 'translateY(-1px)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid rgba(255,255,255,0.9)',
                    outlineOffset: '2px',
                  },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: blueSectionHeadingFontSize, lineHeight: 1.1, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.86)' }}>
                    Medical Conditions
                  </Typography>
                  <Typography sx={{ mt: 0.28, fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {medicalConditionsSummary}
                  </Typography>
                </Box>
                <KeyboardArrowDownRoundedIcon
                  sx={{
                    fontSize: 18,
                    color: '#fff',
                    flexShrink: 0,
                    transform: isMedicalConditionsPopoverOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.18s ease',
                  }}
                />
          </Box>

          <Box
            sx={{
              gridColumn: '1 / -1',
              borderRadius: '12px',
              border: isAnthropometricExpanded ? '1px solid rgba(255,255,255,0.54)' : '1px solid rgba(255,255,255,0.34)',
              px: 1.4,
              py: isAnthropometricExpanded ? 1.7 : 1.3,
              background: isAnthropometricExpanded
                ? 'transparent'
                : 'rgba(255,255,255,0.08)',
              boxShadow: isAnthropometricExpanded
                ? 'inset 0 1px 0 rgba(255,255,255,0.14), 0 12px 28px rgba(2, 92, 132, 0.12)'
                : 'inset 0 1px 0 rgba(255,255,255,0.14)',
              ...(!isAnthropometricExpanded ? bluePanelGlassSx : {}),
              transition: 'background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease',
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setIsAnthropometricExpanded((current) => !current)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                setIsAnthropometricExpanded((current) => !current);
              }}
              sx={{
                minHeight: 38,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                cursor: 'pointer',
                outline: 'none',
                borderRadius: '8px',
                transition: 'background-color 0.18s ease',
                '&:focus-visible': {
                  outline: '2px solid rgba(255,255,255,0.9)',
                  outlineOffset: '2px',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  color: '#fff',
                }}
              >
                Anthropometric Data
              </Typography>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <IconButton
                  size="small"
                  aria-describedby={anthropometricEditPopoverId}
                  aria-label="Edit anthropometric data"
                  title="Edit anthropometric data"
                  onClick={(event) => {
                    event.stopPropagation();
                    hydrateAnthropometricEditData();
                    setAnthropometricEditAnchorEl(event.currentTarget);
                  }}
                  sx={{
                    width: 32,
                    height: 32,
                    padding: 0.5,
                    color: '#fff',
                    borderRadius: 0,
                    border: 'none',
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                    backdropFilter: 'none',
                    WebkitBackdropFilter: 'none',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', border: 'none' },
                    '&:focus-visible': {
                      outline: '2px solid rgba(255,255,255,0.9)',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    border: '1px solid rgba(255,255,255,0.36)',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    ...bluePanelGlassSx,
                    transition: 'transform 0.18s ease',
                  }}
                >
                  <KeyboardArrowDownRoundedIcon
                    sx={{
                      fontSize: 20,
                      color: '#fff',
                      transform: isAnthropometricExpanded ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.18s ease',
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {isAnthropometricExpanded && (
            <>
            <Box
              sx={{
                mt: 1.9,
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                  lg: 'repeat(4, minmax(0, 1fr))',
                  xl: 'repeat(6, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {anthropometricItems.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    minWidth: 0,
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.32)',
                    bgcolor: 'rgba(255,255,255,0.08)',
                    p: 0.9,
                    minHeight: cardHeight,
                    height: cardHeight,
                    display: 'grid',
                    alignItems: 'center',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
                    ...bluePanelGlassSx,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: blueSectionHeadingFontSize,
                      lineHeight: 1.1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'rgba(255,255,255,0.86)',
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.28,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      lineHeight: 1.2,
                      color: '#fff',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.24)', mt: 2, pt: 1.65 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  color: '#fff',
                }}
              >
                Activity and Goals
              </Typography>

              <Box
                sx={{
                  mt: 1.8,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 210px), 1fr))',
                  gap: 1,
                }}
              >
                {displayedActivityAndGoalItems.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      minWidth: 0,
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.32)',
                      bgcolor: 'rgba(255,255,255,0.08)',
                      p: 0.9,
                      minHeight: cardHeight,
                      height: 'auto',
                      display: 'grid',
                      alignContent: 'center',
                      alignItems: 'start',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
                      ...bluePanelGlassSx,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: blueSectionHeadingFontSize,
                        lineHeight: 1.1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: 'rgba(255,255,255,0.86)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.28,
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        lineHeight: 1.2,
                        color: '#fff',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.24)', mt: 2, pt: 1.65 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  color: '#fff',
                }}
              >
                Symptoms and Subscription
              </Typography>

              <Box sx={{ mt: 1.8, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 210px), 1fr))', gap: 1 }}>
                {symptomItems.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      minWidth: 0,
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.32)',
                      bgcolor: 'rgba(255,255,255,0.08)',
                      p: 0.9,
                      minHeight: cardHeight,
                      height: 'auto',
                      display: 'grid',
                      alignContent: 'center',
                      alignItems: 'start',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
                      ...bluePanelGlassSx,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: blueSectionHeadingFontSize,
                        lineHeight: 1.1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: 'rgba(255,255,255,0.86)',
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.28,
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        lineHeight: 1.2,
                        color: '#fff',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
                <Box
                  sx={{
                    minWidth: 0,
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.32)',
                    bgcolor: 'rgba(255,255,255,0.08)',
                    p: 0.9,
                    minHeight: cardHeight,
                    height: 'auto',
                    display: 'grid',
                    alignContent: 'center',
                    alignItems: 'start',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16)',
                    ...bluePanelGlassSx,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: blueSectionHeadingFontSize,
                      lineHeight: 1.1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'rgba(255,255,255,0.86)',
                    }}
                  >
                    Subscription Status
                  </Typography>
                  {activeSubscriptionSummary ? (
                    <>
                      <Typography
                        sx={{
                          mt: 0.28,
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          lineHeight: 1.2,
                          color: '#fff',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {activeSubscriptionSummary.periodLabel}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.15,
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.72rem',
                          lineHeight: 1.2,
                          color: 'rgba(255,255,255,0.82)',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {activeSubscriptionSummary.servicesLabel}
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      sx={{
                        mt: 0.28,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        lineHeight: 1.2,
                        color: 'rgba(255,255,255,0.82)',
                      }}
                    >
                      No active subscription
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            </>
            )}
          </Box>
        </Box>

        <Popover
          id={anthropometricEditPopoverId}
          open={isAnthropometricEditPopoverOpen}
          anchorEl={anthropometricEditAnchorEl}
          onClose={closeAnthropometricEditPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableAutoFocus
          disableRestoreFocus
          PaperProps={{
            sx: {
              mt: 1,
              width: 560,
              maxWidth: 'calc(100vw - 48px)',
              borderRadius: '16px',
              border: '1px solid rgba(32, 174, 220, 0.2)',
              boxShadow: '0 24px 52px rgba(15, 23, 42, 0.22)',
              overflow: 'hidden',
            },
          }}
        >
          <Box sx={{ bgcolor: '#fff' }}>
            <Box
              sx={{
                px: 2.25,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                background: 'linear-gradient(180deg, #f4fbfe 0%, #ffffff 100%)',
                borderBottom: '1px solid #e6eef4',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '1.08rem', lineHeight: 1.2, color: '#101828' }}>
                  Edit Anthropometric Data
                </Typography>
                <Typography sx={{ mt: 0.4, fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', lineHeight: 1.35, color: '#667085' }}>
                  BMI, WHR, IBW, BMR and EER update from saved values.
                </Typography>
              </Box>
              <IconButton
                aria-label="Close anthropometric data editor"
                onClick={closeAnthropometricEditPopover}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: '#20AEDC',
                  color: '#fff',
                  '&:hover': { bgcolor: '#1297c4' },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>

            <Box
              sx={{
                p: 2.25,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                columnGap: 2.15,
                rowGap: 1.75,
              }}
            >
              {([
                ['weightKg', 'Weight (kg)'],
                ['heightCm', 'Height (cm)'],
                ['waistCircumferenceCm', 'Waist (cm)'],
                ['hipCircumferenceCm', 'Hip (cm)'],
              ] as const).map(([field, label]) => (
                <TextField
                  key={field}
                  label={label}
                  type="number"
                  value={anthropometricEditData[field]}
                  onChange={(event) => updateAnthropometricEditField(field, event.target.value)}
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, step: 'any' }}
                  sx={anthropometricEditFieldSx}
                />
              ))}

              {!isInfantForActivity && (
                <TextField
                  select
                  label="Physical activity"
                  value={anthropometricEditData.activityLevel}
                  onChange={(event) => updateAnthropometricEditField('activityLevel', event.target.value)}
                  size="small"
                  fullWidth
                  required={isActivityLevelRequired}
                  disabled={isLoadingFormulaOptions || activityLevelOptions.length === 0}
                  sx={anthropometricEditFieldSx}
                >
                  {activityLevelOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {showPrimaryWeightGoalEditor && (
                <TextField
                  select
                  label="Weight goal"
                  value={anthropometricEditData.weightGoal}
                  onChange={(event) => updateAnthropometricEditField('weightGoal', event.target.value)}
                  size="small"
                  fullWidth
                  sx={anthropometricEditFieldSx}
                >
                  {generalWeightGoalOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                label="Health goal"
                value={anthropometricEditData.healthGoal}
                onChange={(event) => updateAnthropometricEditField('healthGoal', event.target.value)}
                placeholder="Enter health goal"
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={{ ...anthropometricEditFieldSx, gridColumn: { xs: '1 / -1', sm: '1 / -1' } }}
              />

              <TextField
                select
                label="Calculation formula"
                value={anthropometricEditData.formulaId}
                onChange={(event) => updateAnthropometricEditField('formulaId', event.target.value)}
                size="small"
                fullWidth
                required
                disabled={isLoadingFormulaOptions}
                sx={anthropometricEditFieldSx}
              >
                {availableCalculationFormulas.map((formula) => (
                  <MenuItem key={formula.id} value={String(formula.id)}>
                    {formula.displayName || formula.label || formula.formulaName || formula.id}
                  </MenuItem>
                ))}
              </TextField>

              {shouldShowGrowthFactorEditor && (
                <Box
                  sx={{
                    gridColumn: { xs: '1 / -1', sm: 'auto' },
                    minHeight: 40,
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '10px',
                    border: '1px solid #d9e4ea',
                    px: 1.35,
                    bgcolor: '#fff',
                  }}
                >
                  <FormControlLabel
                    sx={{
                      m: 0,
                      width: '100%',
                      justifyContent: 'space-between',
                      gap: 1,
                      '& .MuiFormControlLabel-label': {
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#1D2B2D',
                      },
                    }}
                    label="With growth factor"
                    labelPlacement="start"
                    control={
                      <Switch
                        checked={anthropometricEditData.applyGrowthFactor}
                        onChange={(event) => updateAnthropometricEditField('applyGrowthFactor', event.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#20AEDC',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: '#20AEDC',
                          },
                        }}
                      />
                    }
                  />
                </Box>
              )}

              {shouldShowPregnancyRelatedFields && (
                <>
                  <TextField
                    select
                    label="Pregnancy status"
                    value={anthropometricEditData.pregnancyStatus}
                    onChange={(event) => updateAnthropometricEditField('pregnancyStatus', event.target.value)}
                    size="small"
                    fullWidth
                    sx={anthropometricEditFieldSx}
                  >
                    <MenuItem value="pregnant">Currently Pregnant</MenuItem>
                    <MenuItem value="none">Not Pregnant</MenuItem>
                  </TextField>

                  {isAnthropometricEditPregnant && (
                    <>
                      <TextField
                        select
                        label="Pregnancy trimester"
                        value={anthropometricEditData.pregnancyTrimester}
                        onChange={(event) => updateAnthropometricEditField('pregnancyTrimester', event.target.value)}
                        size="small"
                        fullWidth
                        sx={anthropometricEditFieldSx}
                      >
                        {PREGNANCY_TRIMESTER_OPTIONS.filter((option) => option.value !== 'none').map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Pre-pregnancy weight (kg)"
                        type="number"
                        value={anthropometricEditData.prePregnancyWeightKg}
                        onChange={(event) => updateAnthropometricEditField('prePregnancyWeightKg', event.target.value)}
                        size="small"
                        fullWidth
                        required
                        inputProps={{ min: 0, step: 'any' }}
                        sx={anthropometricEditFieldSx}
                      />
                    </>
                  )}

                  <TextField
                    select
                    label="Lactation status"
                    value={anthropometricEditData.lactationPeriod}
                    onChange={(event) => updateAnthropometricEditField('lactationPeriod', event.target.value)}
                    size="small"
                    fullWidth
                    sx={anthropometricEditFieldSx}
                  >
                    {LACTATION_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  {showPregnancyTypeEditor && (
                  <TextField
                    select
                    label="Pregnancy type"
                    value={anthropometricEditData.isTwinPregnancy ? 'twin' : 'singleton'}
                    onChange={(event) => updateAnthropometricEditField('isTwinPregnancy', event.target.value === 'twin')}
                    size="small"
                    fullWidth
                    sx={anthropometricEditFieldSx}
                  >
                    {PREGNANCY_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  )}

                  {showLactationWeightGoalEditor && (
                    <TextField
                      select
                      label="Weight loss goal"
                      value={anthropometricEditData.weightGoal}
                      onChange={(event) => updateAnthropometricEditField('weightGoal', event.target.value)}
                      size="small"
                      fullWidth
                      sx={anthropometricEditFieldSx}
                    >
                      {lactationWeightGoalOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </>
              )}
            </Box>

            <Box sx={{ px: 2.25, py: 1.75, display: 'flex', justifyContent: 'flex-end', gap: 1, borderTop: '1px solid #eef3f6', bgcolor: '#fbfdfe' }}>
              <Button
                variant="outlined"
                onClick={closeAnthropometricEditPopover}
                disabled={isSavingAnthropometricData}
                sx={{
                  borderRadius: '10px',
                  px: 2.3,
                  color: '#20AEDC',
                  borderColor: '#20AEDC',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  '&:hover': { borderColor: '#1297c4', bgcolor: 'rgba(32, 174, 220, 0.08)' },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveAnthropometricData}
                disabled={isSavingAnthropometricData}
                sx={{
                  minWidth: 128,
                  borderRadius: '10px',
                  px: 2.4,
                  bgcolor: '#20AEDC',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  boxShadow: '0 10px 22px rgba(32, 174, 220, 0.24)',
                  '&:hover': { bgcolor: '#1297c4', boxShadow: '0 12px 26px rgba(18, 151, 196, 0.26)' },
                }}
              >
                {isSavingAnthropometricData ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Popover>

        <Popover
          id={clientGoalsPopoverId}
          open={isClientGoalsOpen}
          anchorEl={activeClientGoalsAnchorEl}
          onClose={closeClientGoalsPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableAutoFocus
          disableRestoreFocus
          PaperProps={{
            sx: {
              mt: 1.05,
              borderRadius: '10px',
              overflow: 'visible',
              width: { xs: 'calc(100vw - 2.25rem)', md: 'min(100vw - 5rem, 760px)' },
              maxWidth: '760px',
              border: '1px solid #d5d9e0',
              boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -8,
                right: 52,
                width: 16,
                height: 16,
                bgcolor: '#fff',
                borderTop: '1px solid #d5d9e0',
                borderLeft: '1px solid #d5d9e0',
                transform: 'rotate(45deg)',
              },
            },
          }}
        >
          <Box sx={{ p: 0, bgcolor: '#fff', borderRadius: '10px', overflow: 'hidden' }}>
            <Box sx={{ px: 1.8, py: 1.15, borderBottom: '1px solid #EAECF0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', fontWeight: 700, color: '#1f2937' }}>
                Client Goals
              </Typography>
              <IconButton
                aria-label="Close client goals"
                onClick={closeClientGoalsPopover}
                sx={{ width: 26, height: 26, bgcolor: '#20AEDC', color: '#fff', '&:hover': { bgcolor: '#1297c4' } }}
              >
                <CloseRoundedIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>

            <Box
              sx={{
                px: 1.7,
                py: 1.7,
                maxHeight: { xs: 'calc(100vh - 13rem)', md: 580 },
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#20AEDC', borderRadius: 999 },
              }}
            >
              <Box
                data-demo-id="meal-plan-calorie-target-editor"
                sx={{ borderRadius: '9px', bgcolor: '#eaf6ff', px: 1.25, py: 0.95, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 132px', alignItems: 'center', gap: 1 }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', fontWeight: 700, color: '#334155' }}>
                      Daily Calorie Goal (EER)
                    </Typography>
                    <Tooltip
                      arrow
                      title="Uses the saved daily calorie target. If protein, carbohydrate, or total fat changes, the target updates from those energy-providing macros; fibre remains a separate intake target."
                    >
                      <IconButton aria-label="How EER is calculated" size="small" sx={{ p: 0.2, color: '#64748B' }}>
                        <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography sx={{ mt: 0.15, fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98A2B3' }}>
                    Automatically updates as the macro goals change
                  </Typography>
                </Box>
                <TextField
                  data-demo-id="meal-plan-calorie-target-input"
                  value={derivedDailyCalorieGoal ?? ''}
                  disabled
                  type="number"
                  size="small"
                  inputProps={{ 'aria-label': 'Derived daily calorie goal' }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 34,
                      borderRadius: '9px',
                      bgcolor: '#E5E7EB',
                      fontWeight: 800,
                      cursor: 'not-allowed',
                    },
                    '& input': { py: 0, textAlign: 'center', fontSize: '0.98rem', fontWeight: 800 },
                    '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#64748B', cursor: 'not-allowed' },
                    '& fieldset, & .Mui-disabled fieldset': { border: '1px solid #CBD5E1' },
                  }}
                />
              </Box>

              <Box sx={{ mt: 1.3, border: '1px solid #EAECF0', borderRadius: '10px', overflow: 'hidden' }}>
                <Typography sx={{ px: 1.55, py: 1, fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', fontWeight: 800, color: '#1f2937' }}>
                  Macronutrients
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1.35fr 2fr', borderTop: '1px solid #EAECF0', borderBottom: '1px solid #EAECF0', bgcolor: '#fff' }}>
                  {['Nutrient', 'Percentage', 'Clinical Limit / Reference', 'Client Goal'].map((header) => (
                    <Typography key={header} sx={{ px: 1.35, py: 0.65, fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#98A2B3' }}>
                      {header}
                    </Typography>
                  ))}
                </Box>
                {editableClientGoalRows.map((row) => {
                  const isEditable = row.nutrient === 'Protein' || row.nutrient === 'Carbohydrate' || row.nutrient === 'Fiber' || row.nutrient === 'Total Fat';
                  const hasKcal = row.kcal != null;
                  const macroConfig = clientGoalRowNutrientMap[row.nutrient];
                  const displayedPercentage = calculateDerivedEnergyPercentage(row, derivedDailyCalorieGoal) ?? row.percentage;

                  return (
                  <React.Fragment key={row.nutrient}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1.35fr 2fr', alignItems: 'center', borderBottom: isEditable ? 'none' : '1px solid #F2F4F7', minHeight: 44 }}>
                    <Typography sx={{ px: 1.35, fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', fontWeight: 700, color: '#1f2937' }}>{row.nutrient}</Typography>
                    <Typography sx={{ px: 1.35, fontFamily: 'Inter, sans-serif', fontSize: '0.76rem', color: '#98A2B3' }}>{displayedPercentage}</Typography>
                    <Typography sx={{ px: 1.35, fontFamily: 'Inter, sans-serif', fontSize: '0.76rem', color: '#475569' }}>{row.referenceRange}</Typography>
                    <Box sx={{ px: 1.35, display: 'flex', alignItems: 'center', gap: 0.45 }}>
                      {isEditable ? (
                        <TextField
                          value={row.grams}
                          onChange={(event) => updateEditableGoalRow(row.nutrient, 'grams', event.target.value)}
                          type="number"
                          size="small"
                          inputProps={{ min: 0, step: row.nutrient === 'Protein' || row.nutrient === 'Total Fat' ? 0.1 : 1 }}
                          sx={{
                            width: 70,
                            '& .MuiInputBase-root': { height: 30, borderRadius: '8px', fontWeight: 800, color: '#20BFB8' },
                            '& input': { px: 0.6, py: 0, textAlign: 'center', fontSize: '0.85rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
                          }}
                        />
                      ) : (
                        <Box sx={{ minWidth: 70, height: 30, borderRadius: '8px', border: '1px solid #CBD5E1', display: 'grid', placeItems: 'center', color: '#20BFB8', fontSize: '0.85rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{row.grams || '--'}</Box>
                      )}
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98A2B3' }}>{row.unit || 'g'}</Typography>
                      {hasKcal && (
                        <>
                          {isEditable && row.nutrient !== 'Fiber' ? (
                            <TextField
                              value={row.kcal}
                              onChange={(event) => updateEditableGoalRow(row.nutrient, 'kcal', event.target.value)}
                              type="number"
                              size="small"
                              inputProps={{ min: 0, step: 1 }}
                              sx={{
                                width: 78,
                                '& .MuiInputBase-root': { height: 30, borderRadius: '8px', fontWeight: 800, color: '#20BFB8' },
                                '& input': { px: 0.6, py: 0, textAlign: 'center', fontSize: '0.85rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
                              }}
                            />
                          ) : (
                            <Box sx={{ minWidth: 78, height: 30, borderRadius: '8px', border: '1px solid #CBD5E1', display: 'grid', placeItems: 'center', color: '#20BFB8', fontSize: '0.85rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{row.kcal || '--'}</Box>
                          )}
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98A2B3' }}>kcal</Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                  {isEditable && macroConfig ? (
                    <Box
                      sx={{
                        mx: 1.25,
                        mb: 0.8,
                        px: 1,
                        py: 0.65,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.6,
                        border: '1px solid #CFFAFE',
                        borderRadius: '7px',
                        bgcolor: '#ECFEFF',
                      }}
                    >
                      <InfoOutlinedIcon sx={{ mt: 0.05, flexShrink: 0, fontSize: 14, color: '#0891B2' }} />
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.7rem', lineHeight: 1.4, color: '#475569' }}>
                        {getMacroGoalHint(client, macroConfig.nutrient)}
                      </Typography>
                    </Box>
                  ) : null}
                  </React.Fragment>
                  );
                })}
              </Box>

              <Box sx={{ mt: 1.15, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={saveClientGoalsFromDialog}
                  disabled={isSavingClientGoals}
                  sx={{ height: 36, minWidth: 145, borderRadius: '8px', bgcolor: '#20AEDC', fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', fontWeight: 700, '&:hover': { bgcolor: '#1297c4' } }}
                >
                  {isSavingClientGoals ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>

        <Box sx={{ height: 20 }} />
      </Box>

      {ongoingVisitBanner ? (
      <Box
        sx={{
          px: 2.5,
          py: 0.85,
          borderBottom: '1px solid #C7F1FF',
          bgcolor: '#E6F9FF',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: '#1d4ed8' }} />
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.75rem',
                lineHeight: 1.1,
                color: '#334155',
              }}
            >
              {formatDate(ongoingVisitBanner.start, 'dd MMM yyyy', '—')}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '0.75rem',
              lineHeight: 1.1,
              color: '#20AEDC',
            }}
          >
            {formatDate(ongoingVisitBanner.start, 'hh:mm a', '—')}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: '0.75rem',
              lineHeight: 1.1,
              color: '#475467',
            }}
          >
            Visit in progress
          </Typography>
        </Box>
      </Box>
      ) : null}

      {!hideTabs ? (
        <Box
          sx={{
            px: 2,
            pb: 0,
            overflowX: 'auto',
            bgcolor: '#fff',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, nextValue: number) => onTabChange(nextValue)}
            variant="scrollable"
            allowScrollButtonsMobile
            scrollButtons="auto"
            sx={{
              minHeight: 48,
              '& .MuiTabs-flexContainer': {
                gap: 2.4,
              },
              '& .MuiTab-root': {
                minHeight: 48,
                minWidth: 0,
                width: 'auto',
                maxWidth: 'none',
                px: 0.75,
                py: 1,
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.8125rem',
                fontWeight: 400,
                lineHeight: 1.35,
                letterSpacing: '-0.01em',
                color: '#6A7282',
                whiteSpace: 'nowrap',
              },
              '& .Mui-selected': {
                color: '#0f8bc7',
                fontWeight: 400,
              },
              '& .MuiTabs-indicator': {
                bottom: 0,
                height: 2.5,
                borderRadius: 999,
                bgcolor: '#25a8da',
              },
            }}
          >
            {tabs.map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>
        </Box>
      ) : null}

      <FormulaValidationModal
        open={showFormulaValidationModal}
        onOpenChange={setFormulaValidationModalOpen}
        selectedFormulaName={nonViableFormulaName}
        fallbackFormulaName={formulaValidationFallbackName}
        onConfirm={handleFormulaValidationConfirm}
      />
    </Paper>
  );
};
